import React from 'react';
import tw, { theme } from 'twin.macro';
import { css } from '@emotion/react';
import { t } from '@lingui/macro';
import { Key, Shortcut } from '@shopify/react-shortcuts';
import { motion, useAnimationControls, useMotionValue } from 'framer-motion';
import { EventCallback } from '@vimeo/player';
import { isAndroid } from 'react-device-detect';

import {
  getInteractionsHash,
  InteractionDisplayState,
  InteractionInfo,
} from '$/components/Interactions/InteractionInfo';
import Loading from '$/components/Loading';
import { StyleProps } from '$/components/ui';
import {
  ActionsPaneStates,
  ActiveInteraction,
  InteractionStateType,
  PlaySeekBlocked,
  TimelineComponents,
  WebinarDimensions,
  WebinarEditMode,
  WebinarPlayerContext,
  WebinarPlayerState,
  WebinarPlayerUIState,
  withWebinarPlayerContext,
} from '$/components/WebinarPlayer/context';
import {
  HasJoined,
  PlayerMode,
  PlayerPlacement,
  VideoPlayerDimensionsOptions,
} from '$/components/WebinarPlayer/types';
import { FBPixelEvent } from '$/FBPixel';
import {
  useGetInteractionsForAttendeeQuery,
  useGetInteractionsInSetQuery,
  usePlaybackEventMutation,
  useWebinarVideoResponseLazyQuery,
} from '$/GraphQL/schema/schema';
import {
  AttendeeFragment,
  ChatType,
  InteractionForAttendeeFragment,
  InteractionFragment,
  InteractionType,
  InteractionValueType,
  MeFragment,
  PlaybackEventType,
  PublicEWebinarFragment,
  PublicSetFragment,
  ResultsAppearAt,
  RoomType,
  WatchedSegment,
} from '$/GraphQL/schema/types';
import { isEqualDeep, useHasChangedShallow } from '$/hooks/useHasChanged';
import { useMemoized } from '$/hooks/useMemoized';
import { useRefFromState } from '$/hooks/useRefFromState';
import useRunOnce from '$/hooks/useRunOnce';
import { useServerContext } from '$/hooks/useServerDateNow';
import { withContextValue } from '$/hooks/withPartialContexts';
import { useSSOAuth } from '$/sections/Home/Profile/TeamIntegrations/Info/SSO/SSOLogin';
import EDate, { importDateLocale } from '$/shared/frontend/utils/EDate';
import { getLocaleForLang } from '$/shared/frontend/utils/i18n';
import { sendPlaybackDataToCypress } from '$/utils/e2e/events';
import { ErrorReport } from '$/utils/ErrorReport';
import { importWithRetry } from '$/utils/import';
import isMobile from '$/utils/isMobile';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { logger } from '$/utils/logger';
import { EventPerfDetails, eventPerfEnd, eventPerfStart } from '$/utils/logPerfHelper';
import { generateId } from '$/utils/number';
import { withProperties } from '$/utils/type';
import { useFBPixelContext } from '$/utils/useFBPixel';
import { useDebouncedCallback } from 'use-debounce';
import { WaitFor, WebinarPlayerRenderProvider } from '../DelayRender';
import { useInteractionMatchDisplayRulesCallback } from '../Interactions/ActionProperty/useInteractionMatchDisplayRulesCallback';
import { useInteractionFactory } from '../Interactions/useInteractionFactory';
import {
  AttendeePropertiesProvider,
  useAttendeePropertiesContext,
} from '../Property/PropertyProvider';
import { PropertyFilledDataContextProvider } from '../ui/EditableInput/DangerouslySetInnerHTML';
import { useThemeContext } from '../ui/ThemeContext';
import { BackgroundGradient } from './BackgroundGradient';
import { VideoControlsBar } from './Controls/ControlBars';
import InteractionsStream, {
  ActionsPaneToggleButton,
} from './InteractionsStream/InteractionsStream';
import { PinnedCardBar } from './InteractionsStream/PinnedCardBar';
import { JoinRoom, RoomParts } from './Rooms/RoomParts';
import { Rooms } from './Rooms/Rooms';
import Timeline from './Timeline/Timeline/Timeline';
import { Transitions } from './transitions';
import useVideoPlayerDimensions from './useVideoPlayerDimensions';
import { useMe } from '$/hooks/useMe';
import { useEmbeddedPlayerContext } from '../EmbeddedPlayer/useEmbeddedPlayer';
import { useAttendeeContext } from '$/sections/PublishedWebinar/ViewWebinar/AttendeeContext';

export const InteractionCardsReverseScroll = false;
const emptyFragmentsArray: (InteractionFragment | InteractionForAttendeeFragment)[] = [];

// Define initial volume of webinar player
export const INITIAL_VOLUME = 1;

type PlayerError = Error & {
  message?: string;
  name?: string;
  method?: string;
};

const colors = {
  room: '#2A454A',
  presentationRoom: '#537175',
  text: '#F8FAFA',
  controlBg: '#0E282D',
  control: '#FF7470',
  controlHover: '#ff5752',
  sliderBg: '#537175',
  sliderTrackBg: '#fff',
  zoomSliderBg: '#537175',
};

const useWebinarPlayer = ({
  hideTimelineComponents,
  showTimelineComponents,
  mode,
  placement = PlayerPlacement.EWebinarPage,
  attendee,
  editMode,
  set,
  ewebinar,
  onInteractionIconClick,
  onZoomScaleChange,
  isEditingInteractions,
  interactionInEdit,
  inTestMode,
}: WebinarPlayerProps): Omit<WebinarPlayerState, 'animations'> => {
  const [focusedCardMode, setFocusedCardMode] = React.useState(false);
  const [agendaMenuShowed, setAgendaMenuShowed] = React.useState(false);
  const [showOverlay, setShowOverlay] = React.useState(false);
  const [attendeeCount, setAttendeeCount] = React.useState(1);
  const [interactionState, setInteractionState] = React.useState<InteractionStateType>(
    {} as InteractionStateType
  );
  const [enabledCC, setEnabledCC] = React.useState(false);

  const [stateModalClose, setStateModalCloseInternal] = React.useState(false);
  const setStateModalClose = React.useCallback((val: boolean): void => {
    setStateModalCloseInternal(val);
  }, []);

  const { isExpanded, isEmbeddedPlayer, playerInfo } = useEmbeddedPlayerContext();
  const isAnonymousAttendee = attendee?.id === 'anonymous';

  const interactionsForAttendeeQuery = useGetInteractionsForAttendeeQuery({
    variables: {
      ewebinarId: String(ewebinar.id),
      attendeeId: String(attendee?.id),
    },
    skip: !attendee?.id || isAnonymousAttendee,
  });

  const [includeLegacyInteractions, setIncludeLegacyInteractions] = React.useState(false);
  const [interactionInEditModalId, setInteractionInEditModalId] = React.useState<
    string | undefined
  >();
  const interactionsForSetQuery = useGetInteractionsInSetQuery({
    variables: {
      ewebinarSetId: `${set.id}`,
      includeDraft: editMode === 'edit' || editMode === 'edit.modal',
      includePublished: editMode !== 'edit' && editMode !== 'edit.modal',
      includeLegacyInteractions,
    },
    fetchPolicy: 'network-only',
    skip: Boolean(attendee?.id) && !isEmbeddedPlayer, // use interactions of the public webinar for anonymous attendees until a real attendee is created and interactionsForAttendeeQuery is loaded successfully
  });
  const [vimeoDuration, setVimeoDuration] = React.useState<number>(ewebinar?.duration ?? 45 * 60);
  const [connectionId, setConnectionId] = React.useState('');
  const shortIdRef = React.useRef(generateId());
  const [locale, setLocale] = React.useState<Locale | undefined>();
  const blockSeekAndPlayRef = React.useRef<PlaySeekBlocked>({ type: 'none' });

  useRunOnce(() => {
    if (locale) return;
    async function doAsync(): Promise<void> {
      const locale = getLocaleForLang(ewebinar.viewerRoomSettings.language ?? 'en');
      setLocale(await importDateLocale(locale));
    }

    doAsync();
  });

  const me = useMe() as MeFragment | undefined;
  const isCollapsedEmbeddedPlayer = isEmbeddedPlayer && !isExpanded;
  const interactionFactory = useInteractionFactory(Boolean(attendee), me);

  const dataLoaded =
    (!interactionFactory.loading &&
      interactionsForSetQuery.called &&
      !interactionsForSetQuery.loading &&
      Boolean(ewebinar)) ||
    (!interactionFactory.loading &&
      interactionsForAttendeeQuery.called &&
      !interactionsForAttendeeQuery.loading &&
      Boolean(ewebinar));

  const lastSegment =
    mode === PlayerMode.edit
      ? undefined
      : mode === PlayerMode.replay
        ? attendee?.lastReplaySegment
        : attendee?.lastWatchedSegment;
  const lastSegmentRef = React.useRef(lastSegment);
  const { serverDateNow } = useServerContext();

  if (!lastSegmentRef.current && attendee?.startTime) {
    const now = serverDateNow();

    lastSegmentRef.current = {
      startTime: now.toISOString(),
      startAtSecs: new EDate(now, 'UTC').diffInSeconds(new EDate(attendee.startTime, 'UTC')),
      speed: 1,
    };
    logger.debug('PLAY', 'CREATING LASTSEGMENT BASED ON STARTTIME ', { ...lastSegmentRef.current });
  }
  const setLastSegment = React.useCallback((segment: Omit<WatchedSegment, 'cid'>) => {
    const currentUpdatedAt = Math.max(
      new Date(lastSegmentRef.current?.endTime ?? 0).getTime() -
        (lastSegmentRef.current?.extendedBySecs ?? 0) * 1000,
      new Date(lastSegmentRef.current?.startTime ?? 0).getTime()
    );

    const newUpdatedAt = Math.max(
      new Date(segment.endTime ?? 0).getTime(),
      new Date(segment.startTime).getTime()
    );

    if (newUpdatedAt > currentUpdatedAt) {
      lastSegmentRef.current = segment;
    }
  }, []);

  React.useEffect(() => {
    if (!lastSegment) return;

    logger.debug('PLAY', '288 UPDATING LS ', lastSegment);
    // Update lastSegment we're using to attendee's newest lastSegment
    setLastSegment(lastSegment);
  }, [lastSegment, setLastSegment]);

  const watchingReplay = mode === PlayerMode.replay;

  const rooms = React.useMemo(() => {
    return Rooms.create(
      {
        duration: ewebinar.duration,
        exitRoomDurationSecs: ewebinar.exitRoomDurationSecs,
        waitingRoomDurationSecs: ewebinar.waitingRoomDurationSecs,
      },
      vimeoDuration
    );
  }, [
    ewebinar.duration,
    ewebinar.exitRoomDurationSecs,
    ewebinar.waitingRoomDurationSecs,
    vimeoDuration,
  ]);

  // Expire replay link
  let isExpired = false;
  if (attendee?.startTime && set.expireReplayLinkAfterMins && mode === PlayerMode.replay) {
    const date = new EDate(attendee?.startTime, 'UTC').addMinutes(set.expireReplayLinkAfterMins);
    isExpired = date.diffInSeconds(new EDate(serverDateNow(), 'UTC')) <= 0;
  }
  const [isReplayExpired, setIsReplayExpired] = React.useState(isExpired);

  const [muted, setMuted] = React.useState(false);
  const [mutedByJoiningIssue, setMutedByJoiningIssue] = React.useState(false);
  const [volume, setVolume] = React.useState(INITIAL_VOLUME);
  const [hasJoined, setHasJoined] = React.useState(
    mode !== PlayerMode.live ? HasJoined.Joined : HasJoined.NotJoined
  );
  const [joinTime, setJoinTime] = React.useState<Date>();

  // Returns what the playbackPosition should be as well as what the timer seconds should be
  // Returns what the playbackPosition should be as well as what the timer seconds should be
  const calcPlaybackFromLastSegment = React.useCallback(
    (lastSegment: Omit<WatchedSegment, 'cid'>): [number, number, number] => {
      if (attendee?.paused) {
        // Attendee paused permanently, the type of lastSegment is 'Pause'
        // `endAtSecs` is position calling Pause event (equal to the `appearAt` of In-video registration)
        return [lastSegment.endAtSecs ?? 0, 0, 0];
      }
      if (
        lastSegment.extendedBySecs &&
        new Date(lastSegment.endTime!).getTime() > serverDateNow().getTime()
      ) {
        // Last segment had a pause timer &
        // End of timer is in the future
        return [
          lastSegment.endAtSecs!,
          lastSegment.extendedBySecs,
          new EDate(lastSegment.endTime!, 'UTC').diffInSeconds(new EDate(serverDateNow(), 'UTC')),
        ];
      }

      const atSecs = lastSegment.endAtSecs ?? lastSegment.startAtSecs;
      const atTime = lastSegment.endTime ?? lastSegment.startTime;

      return [
        // Must be Math.floor so a video of 800.653 duration ends up at 800 not 801 which is a different room
        Math.floor(
          atSecs +
            Math.ceil((serverDateNow().getTime() - new Date(atTime).getTime()) / 1000) *
              lastSegment.speed
        ),
        0,
        0,
      ];
    },
    [attendee?.paused, serverDateNow]
  );

  let defaultPlaybackPosition = 0; //ewebinar.duration;
  let defaultPauseTimer = 0;
  let defaultPauseTimerRemaining = 0;

  if (watchingReplay) {
    defaultPlaybackPosition = 0;
  } else if (mode !== PlayerMode.edit && !(attendee?.isOnDemand && !attendee?.joinTime)) {
    [defaultPlaybackPosition, defaultPauseTimer, defaultPauseTimerRemaining] =
      lastSegmentRef.current
        ? calcPlaybackFromLastSegment(lastSegmentRef.current)
        : [rooms.domain[0], 0, 0];
  }
  if (interactionInEdit) {
    defaultPlaybackPosition = interactionInEdit.playbackPosition;
  }

  const pauseTimerRemainingRef = React.useRef<number | null>(defaultPauseTimerRemaining);
  const pauseTimerRef = React.useRef(defaultPauseTimer);
  const [pauseTimer, setPauseTimerState] = React.useState(defaultPauseTimer);
  const setPauseTimer = React.useCallback((timer: number): void => {
    pauseTimerRef.current = timer;
    setPauseTimerState(timer);
  }, []);

  const [pauseMessage, setPauseMessage] = React.useState<string | undefined | null>();

  // Tracks the actual time the player is at
  const playerCurrentTimeRef = React.useRef(-1);

  // Tracks the time eWebinar thinks we're at
  const playbackPositionRef = React.useRef(defaultPlaybackPosition);
  // Same as playbackPositionRef but can drive Framer Motion based changes
  // without needing to use React state
  const playbackPositionValue = useMotionValue(defaultPlaybackPosition);
  // Same as playbackPositionRef but can cause hooks to fire on change
  const [playbackPosition, setPlaybackPositionState] =
    React.useState<number>(defaultPlaybackPosition);

  const [scrollToCardOrPositionTarget, scrollToCardOrPosition] = React.useState<
    InteractionInfo | number | 'snap' | null // This is an interaction card OR a playbackPosition to scroll to
  >(null);

  const scrollCardsToPosition = React.useCallback((pos: number) => {
    scrollToCardOrPosition(pos);
  }, []);

  // Jump to RoomType.PostExit if replay expires
  const currentRoom = isReplayExpired
    ? rooms.getRoom(RoomType.PostExit)
    : rooms.getRoomAt(playbackPositionRef.current);
  const [willPlayAfterSeek, setWillPlayAfterSeek] = React.useState(false);
  const isLastSecondOfPresentationRoom =
    currentRoom.type === RoomType.Presentation && vimeoDuration - playbackPosition <= 1;

  const needsJoin = currentRoom.caps.hasVideoPlayer;
  // In PreWaiting we don't join but want the 1second interval so isPlaying needs to be true
  const [isPlaying, setIsPlaying] = React.useState(!needsJoin && mode !== PlayerMode.edit);
  const isPlayingRef = useRefFromState(isPlaying);

  const [actionsPaneState, setActionsPaneState] = React.useState<ActionsPaneStates>(() =>
    editMode && !['dash.analytics', 'dash.chat'].includes(editMode) ? 'shown' : 'offscreen'
  );

  const [isScrobbling, setIsScrobblingState] = React.useState(false);
  const isScrobblingRef = React.useRef(false);
  const setIsScrobbling = React.useCallback((value: boolean) => {
    setIsScrobblingState(value);
    isScrobblingRef.current = value;
  }, []);
  const scrobblingFromRef = React.useRef<number | null>(null);

  const vimeoVideoId = ewebinar.vimeoVideoId;
  const vimeoPlayUrl = ewebinar.videoPlayUrl;
  const vimeoHash = vimeoPlayUrl?.replace(/^.+\//, ''); // Extract security handle form end of URL

  const [totalReactions, setTotalReactions] = React.useState(0);
  const [interactions, setInteractions] = React.useState<InteractionInfo[]>([]);
  const interactionHeightsVersion = useMotionValue(0);
  const [interactionHeights, setInteractionHeights] = React.useState<{ [shortId: string]: number }>(
    {}
  );

  const { conditionalDisplay } = useAttendeePropertiesContext();
  const matchDisplayRulesForInteraction = useInteractionMatchDisplayRulesCallback(
    conditionalDisplay.properties
  );

  const fastInteractionHeights = React.useRef<{
    [shortId: string]: number;
  }>({});
  const setInteractionHeightsDebounced = useDebouncedCallback(
    () => {
      setInteractionHeights(fastInteractionHeights.current);
      interactionHeightsVersion.set(interactionHeightsVersion.get() + 1);
    },
    200,
    { leading: false, trailing: true }
  );

  const setInteractionHeight = React.useCallback(
    (shortId: string, height: number, allowSmallerHeight?: boolean) => {
      /*
        setInteractionHeights causes a lot of relayouts - initially all cards calc their heights
        which should be fast.  So initially we don't want to cause relayouts until all of the
        height values have been set.  So we use fastInteractionHeights as a buffer which doesn't
        cause any relayouts
       */
      if (!shortId) return;
      if (height === -1) {
        // Removing card just forces refresh of heights
        return;
      }
      const oldHeight = fastInteractionHeights.current[shortId] ?? 0;

      if (!allowSmallerHeight) {
        if (height === 0 || Math.abs(oldHeight - height) < 4) {
          // Too small to care
          return;
        }
  
        if (height < oldHeight) {
          // Don't allow height to shrink
          return;
        }
      }

      logger.debug('ANIM', 'New card height: ', {
        shortId: shortId,
        oldHeight,
        height,
      });
      fastInteractionHeights.current = {
        ...fastInteractionHeights.current,
        [shortId]: height,
      };
      setInteractionHeightsDebounced();
    },
    [setInteractionHeightsDebounced]
  );

  const fragments: InteractionForAttendeeFragment[] =
    interactionsForSetQuery.data?.interactionsInEwebinarSet ??
    interactionsForAttendeeQuery.data?.interactions ??
    emptyFragmentsArray;
  const fragmentsLoading = interactionsForSetQuery.loading || interactionsForAttendeeQuery.loading;

  const haveFragmentsChanged = useHasChangedShallow([fragments], { skipInitial: fragmentsLoading });
  React.useEffect((): void => {
    if (!haveFragmentsChanged) return;
    setTotalReactions(fragments.filter((i) => i.reaction).length);

    const infos = fragments
      .map((f) => {
        // DD: We should try and return the same InteractionInfo as we already had
        // as React components can have a reference to the old Info object and we'd
        // like to simply update the Interaction information in that object - otherwise
        // some components are acting on old versions of the interaction fragment
        const interaction = interactions.find(
          (oldi) => oldi.fragment.interactionShortId === f.interactionShortId
        );
        if (interaction) {
          interaction.updateFragment(f);

          // If in edit we don't return and also create a new interaction becuase when editing data
          // inside the interaction the object itself isn't changing and so React isn't updating
          // the components that use it.  This isn't an issue for viewing as the interaction values
          // don't change

          if (mode !== PlayerMode.edit) {
            return interaction;
          }
        }

        return interactionFactory.from(f, ewebinar, attendee);
      })
      .sort((a, b) =>
        editMode && !isPlaying
          ? a.playbackPosition - b.playbackPosition
          : b.playbackPosition - a.playbackPosition
      );

    logger.debug('ANIM', 'Changing interaction info', infos);
    setInteractions(infos);
  }, [
    attendee,
    editMode,
    ewebinar,
    fragments,
    haveFragmentsChanged,
    interactionFactory,
    interactions,
    isPlaying,
    mode,
  ]);

  const endPollOrQuizResults = React.useMemo(() => {
    if (editMode) {
      return [];
    }
    return interactions
      .filter((i): boolean => {
        // Waiting room cards don't show up in replay
        if (mode === PlayerMode.replay && i.playbackPosition < 0) {
          return false;
        }
        if (attendee?.isOnDemand && i.playbackPosition < 0) {
          return false;
        }
        if (![InteractionType.Poll, InteractionType.Quiz].includes(i.type as InteractionType))
          return false;

        if (
          i.type === InteractionType.Quiz &&
          i.fragment.details!.resultsAppearAt === ResultsAppearAt.EndOfPresentation &&
          !i.hasReaction
        )
          return false;

        if (!matchDisplayRulesForInteraction(i)) return false;

        switch (i.fragment.details!.resultsAppearAt) {
          case ResultsAppearAt.EndOfPresentation:
          case ResultsAppearAt.AfterXMinutes:
            return true;
          default:
            return false;
        }
      })
      .map((i) => {
        const showAfterXMinutes =
          i.fragment.details!.resultsAppearAt === ResultsAppearAt.AfterXMinutes;
        let playbackPosition = ewebinar.duration - 10;
        if (showAfterXMinutes) {
          playbackPosition = i.playbackPosition + (i.details?.endsIn ?? 0);
          if (mode === PlayerMode.replay && i.room.type === RoomType.Presentation) {
            playbackPosition = Math.min(playbackPosition, ewebinar.duration - 10);
          }
        }
        const [appearAt, room] = i.rooms.appearAtDuoFromPosition(playbackPosition);
        return interactionFactory.from(
          {
            ...i.fragment,
            id: `${i.id}-result`,
            interactionShortId: `${i.shortId}-result`,
            room,
            appearAt,
            details:
              i.fragment.details && showAfterXMinutes
                ? { ...i.fragment.details, endsIn: 0 }
                : i.fragment.details,
          },
          i.ewebinar
        );
      });
  }, [
    editMode,
    ewebinar.duration,
    interactionFactory,
    interactions,
    matchDisplayRulesForInteraction,
    mode,
    attendee?.isOnDemand,
  ]);

  // Cumulative Quiz results at end of Presentation Room
  const cumulativeQuizResult = React.useMemo(() => {
    const cumulativeQuizes = interactions.filter((i) => {
      return i.type === InteractionType.Quiz && i.details?.showCumulativeResults;
    });

    if (!editMode && cumulativeQuizes.length > 0) {
      return interactionFactory.from(
        {
          id: 'QuizSummary',
          interactionShortId: 'QuizSummary',
          type: InteractionType.QuizSummary,
          valueType: InteractionValueType.Answers,
          room: RoomType.Presentation,
          appearAt: ewebinar.duration - 5,
          createdAt: serverDateNow().toISOString(),
          updatedAt: serverDateNow().toISOString(),
          details: { title: 'Quiz results' },
        },
        ewebinar
      );
    }

    return undefined;
  }, [interactions, editMode, interactionFactory, ewebinar, serverDateNow]);

  const endStream = React.useMemo(
    () =>
      [
        cumulativeQuizResult,
        ...(!editMode || isPlaying
          ? []
          : interactions
              .filter((i) => i.hasEndsAt())
              .map((i) =>
                interactionFactory.from(
                  {
                    id: `${i.id}-endsAt`,
                    interactionShortId: `${i.shortId}-endsAt`,
                    type: i.type,
                    valueType: InteractionValueType.Action,
                    room: RoomType.Presentation,
                    appearAt: i.endsAt()!,
                    createdAt: serverDateNow().toISOString(),
                    updatedAt: serverDateNow().toISOString(),
                    details: { title: 'Ends' },
                  },
                  ewebinar
                )
              )
              .concat(
                interactionFactory.from(
                  {
                    id: 'endStream',
                    interactionShortId: '',
                    type: InteractionType.EndStream,
                    valueType: InteractionValueType.Action,
                    room: RoomType.Exit,
                    appearAt: 0.1, // add 0.1 to make sure it just shows in exit room
                    createdAt: serverDateNow().toISOString(),
                    updatedAt: serverDateNow().toISOString(),
                    details: {
                      title: 'End of presentation',
                    },
                  },
                  ewebinar
                )
              )),
        ...endPollOrQuizResults,
      ].filter((i) => Boolean(i)) as InteractionInfo[],
    [
      cumulativeQuizResult,
      editMode,
      isPlaying,
      interactions,
      interactionFactory,
      serverDateNow,
      ewebinar,
      endPollOrQuizResults,
    ]
  );

  const {
    cardInteractions: newCardInteractions,
    nonCardInteractions: newNonCardInteractions,
    overlaysUnderControls: newOverlaysUnderControls,
    overlaysOverControls: newOverlaysOverControls,
    overlaysOverArea: newOverlaysOverArea,
  } = React.useMemo(() => {
    const isValidInteractions = (interaction: InteractionInfo): boolean => {
      // During Edit all cards are visible unless playing video
      if (editMode && !isPlaying) return true;

      // Waiting room cards don't show up in replay
      if (
        (mode === PlayerMode.replay || attendee?.isOnDemand) &&
        interaction.playbackPosition < 0 &&
        (!interaction.hasEndsAt() || interaction.endsAtPlaybackPosition <= 0)
      ) {
        return false;
      }

      // Agenda items are always valid
      if (interaction.type === InteractionType.Anchor) return true;

      // Interaction in the future - filter for now
      if (interaction.playbackPosition > playbackPosition) return false;

      // Hide interaction if it's invalid
      if (mode !== PlayerMode.edit && interactionState[interaction.shortId]?.invalid) {
        return false;
      }

      return true;
    };

    const sortInteraction = (a: InteractionInfo, b: InteractionInfo): number => {
      return InteractionCardsReverseScroll
        ? b.playbackPosition - a.playbackPosition
        : a.playbackPosition - b.playbackPosition;
    };

    // This recalcs every second
    const validInteractions = interactions.filter(isValidInteractions);
    const validEndStream = endStream.filter(isValidInteractions);

    const isPreview = editMode && isPlaying;

    return {
      cardInteractions: [
        ...validEndStream.filter((i) => (editMode && !isPlaying) || i.hasCard(isPreview)),
        ...validInteractions.filter(
          (i) =>
            (editMode && !isPlaying) ||
            (i.hasCard(isPreview) &&
              !i.shouldRemoveAnsweredCard() &&
              !i.isExpired(playbackPosition, mode) &&
              matchDisplayRulesForInteraction(i))
        ),
      ].sort(sortInteraction),

      nonCardInteractions: validInteractions
        .sort(sortInteraction)
        .filter(
          (i) =>
            !i.hasCard(isPreview) &&
            (!i.hasVideoOverlay() || i.hasVideoOverlay() === 'invisible') &&
            matchDisplayRulesForInteraction(i)
        ),

      overlaysUnderControls: validInteractions
        .sort(sortInteraction)
        .filter(
          (i) =>
            i.id !== interactionInEdit?.id &&
            i.hasVideoOverlay() === 'over-video' &&
            !i.isInteractiveOverlay() &&
            playbackPosition >= i.playbackPosition &&
            playbackPosition < i.endsAtPlaybackPosition &&
            matchDisplayRulesForInteraction(i)
        ),
      overlaysOverControls: validInteractions
        .sort(sortInteraction)
        .filter(
          (i) =>
            i.id !== interactionInEdit?.id &&
            i.hasVideoOverlay() === 'over-video' &&
            i.isInteractiveOverlay() &&
            playbackPosition >= i.playbackPosition &&
            playbackPosition < i.endsAtPlaybackPosition &&
            matchDisplayRulesForInteraction(i)
        ),
      overlaysOverArea: validInteractions
        .sort(sortInteraction)
        .filter(
          (i) =>
            i.id !== interactionInEdit?.id &&
            (i.hasVideoOverlay() === 'over-area' || i.hasVideoOverlay() === 'invisible') &&
            playbackPosition >= i.playbackPosition &&
            playbackPosition < i.endsAtPlaybackPosition &&
            matchDisplayRulesForInteraction(i)
        ),
    };
  }, [
    editMode,
    endStream,
    interactionInEdit?.id,
    interactionState,
    interactions,
    isPlaying,
    matchDisplayRulesForInteraction,
    mode,
    playbackPosition,
    attendee?.isOnDemand,
  ]);

  // This is here so cardInteractions doesn't change every second.  It
  // only changes if the set of interactions or their reactions have actually changed
  // since newCardInteractions & newNonCardInteractions change ever second
  const newCardInteractionsHash = getInteractionsHash(newCardInteractions);
  const [cardInteractions, setCardInteractions] =
    React.useState<InteractionInfo[]>(newCardInteractions);

  const cardInteractionsRef = useRefFromState(cardInteractions);
  cardInteractionsRef.current = cardInteractions;

  const haveCardInteractionsHashChanged = useHasChangedShallow([newCardInteractionsHash]);
  React.useEffect(() => {
    if (!haveCardInteractionsHashChanged) return;

    logger.debug('ANIM', 'Updating interactions in stream', {
      newCardInteractions,
    });
    setCardInteractions(newCardInteractions);
  }, [haveCardInteractionsHashChanged, newCardInteractions]);

  const newNonCardInteractionsHash = newNonCardInteractions.map((i) => i.id).join('-');
  const nonCardInteractions = React.useMemo(() => {
    return newNonCardInteractions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNonCardInteractionsHash]);

  const newOverlaysOverControlsHash = getInteractionsHash(newOverlaysOverControls);
  const overlaysOverControls = React.useMemo(() => {
    return newOverlaysOverControls;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOverlaysOverControlsHash]);

  const newOverlaysUnderControlsHash = getInteractionsHash(newOverlaysUnderControls);
  const overlaysUnderControls = React.useMemo(() => {
    return newOverlaysUnderControls;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOverlaysUnderControlsHash]);

  const newOverlaysOverAreaHash = getInteractionsHash(newOverlaysOverArea);
  const overlaysOverArea = React.useMemo(() => {
    return newOverlaysOverArea;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOverlaysOverAreaHash]);

  const hasInVideoFormShowBeforeWatching = interactions.some((i) => {
    return (
      i.type === InteractionType.InVideoRegistration &&
      currentRoom.type === RoomType.Presentation &&
      i.appearAt() === 0
    );
  });
  const showingInVideoFormBeforeWatching = [...overlaysOverControls, ...overlaysOverArea].some(
    (i) => {
      return (
        i.type === InteractionType.InVideoRegistration &&
        currentRoom.type === RoomType.Presentation &&
        i.appearAt() === 0
      );
    }
  );

  const [willBeRemovedCards, setWillBeRemovedCards] = React.useState<Record<string, boolean>>({});
  const setWillBeRemovedCard = React.useCallback(
    (interactionId: string, willBeRemoved: boolean) => {
      setWillBeRemovedCards((willBeRemovedCards) => ({
        ...willBeRemovedCards,
        [`${interactionId}`]: willBeRemoved,
      }));
    },
    []
  );

  const hideTimelineComponentsState = useMemoized(hideTimelineComponents);
  const showTimelineComponentsState = useMemoized(showTimelineComponents);
  const timelineComponents: TimelineComponents = React.useMemo(() => {
    const components: {
      bar: keyof TimelineComponents;
      height: number;
      excludeWhenEmpty?: boolean;
    }[] = [
      // Order matters
      { bar: 'title', height: 22 },
      { bar: 'imageOverlays', height: interactionInEdit ? 40 : 25, excludeWhenEmpty: true },
      { bar: 'conversions', height: interactionInEdit ? 40 : 25, excludeWhenEmpty: true },
      { bar: 'main', height: interactionInEdit ? 40 : 30 },
      { bar: 'anchor', height: 15 }, // Uses full height of timeline - doesn't use its own space
      { bar: 'time', height: 18 }, // Always there
      { bar: 'scroll', height: 10 }, // Always there
    ];

    const subset: TimelineComponents = {
      // Temporary until below code runs
      anchor: { height: 0, offset: 0 },
      time: { height: 0, offset: 0 },
      scroll: { height: 0, offset: 0 },
    };

    let currentOffset = 0;
    for (const component of components) {
      if (hideTimelineComponentsState?.includes(component.bar)) {
        // Remove this one
        continue;
      }

      if (
        component.excludeWhenEmpty &&
        !interactions.find((i) => i.timelineComponent() === component.bar) &&
        interactionInEdit?.timelineComponent() !== component.bar
      ) {
        continue;
      }

      if (showTimelineComponentsState && !showTimelineComponentsState?.includes(component.bar)) {
        if (!['time', 'scroll'].includes(component.bar)) {
          // Remove this one as not included in showTimelineComponents
          continue;
        }
      }

      subset[component.bar] = {
        height: component.height,
        offset: currentOffset,
      };

      currentOffset += component.height;
    }

    return subset;
  }, [hideTimelineComponentsState, interactionInEdit, interactions, showTimelineComponentsState]);

  const timelineHeight = Object.keys(timelineComponents).reduce(
    (total, key) => (total += timelineComponents[key as keyof TimelineComponents]?.height ?? 0),
    0
  );

  const pinnedInteraction = React.useMemo(() => {
    const pos = Math.floor(playbackPosition);
    const pinnedInteractions = cardInteractions
      .filter(
        (i) =>
          i.isPinned() &&
          (!i.isAnswered() ||
            i.cardDisplayState(pos, mode) === InteractionDisplayState.Normal ||
            i.canSubmitMuktipleTimes()) &&
          (i.playbackPosition >= 0 || mode !== PlayerMode.replay) &&
          !i.isExpired(pos, mode) &&
          i.playbackPosition <= pos
      )
      .sort((a: InteractionInfo, b: InteractionInfo) => {
        return a.playbackPosition - b.playbackPosition;
      });

    return pinnedInteractions.pop();
  }, [cardInteractions, mode, playbackPosition]);

  const chatType = attendee?.sessionType
    ? ewebinar.chatSettings[attendee.sessionType].type
    : undefined;

  inTestMode = Boolean(attendee?.isTest || inTestMode);

  const options = React.useMemo((): VideoPlayerDimensionsOptions => {
    return {
      mode,
      editMode,
      isPlaying,
      actionsPaneState,
      inTestMode: Boolean(inTestMode),
      hasPinnedInteraction: Boolean(pinnedInteraction),
      isEmbeddedPlayer,
      isCollapsedEmbeddedPlayer,
      borderRadius: ewebinar.borderRadius,
      focusedCardMode,
      embeddedPlayerInfo: playerInfo,
      hasChat: currentRoom.hasChat,
      hasInteractions: currentRoom.caps.hasInteractions,
      hasVideoPlayer: currentRoom.caps.hasVideoPlayer,
      showAgenda: currentRoom.caps.showAgenda,
      isEntireChatOff: [ChatType.None, ChatType.ThirdParty].includes(chatType as ChatType),
    };
  }, [
    actionsPaneState,
    currentRoom.caps.hasInteractions,
    currentRoom.caps.hasVideoPlayer,
    currentRoom.caps.showAgenda,
    currentRoom.hasChat,
    editMode,
    ewebinar.borderRadius,
    focusedCardMode,
    inTestMode,
    isCollapsedEmbeddedPlayer,
    isEmbeddedPlayer,
    isPlaying,
    mode,
    pinnedInteraction,
    playerInfo,
    chatType,
  ]);
  const { playerIframeRef, webinarAreaRef, player, dimensions, transitions, blocked } =
    useVideoPlayerDimensions(
      !currentRoom.caps.hasVideoPlayer
        ? null
        : { vimeoVideoId: vimeoVideoId ?? null, vimeoPlayUrl },
      options
    );
  const playerRef = useRefFromState(player);
  const dimensionsRef = useRefFromState(dimensions);

  const [playbackSpeed, setPlaybackSpeedInternal] = React.useState(1);
  const setPlaybackSpeed = React.useCallback(
    (rate: number): void => {
      if (!player) return;

      setPlaybackSpeedInternal(rate);
      player?.setPlaybackRate(rate);
    },
    [player]
  );

  const [loaded, setLoaded] = React.useState(false);

  // Tracks how long it takes user to see a Join button after Loaded
  const joinButtonPerfRef = React.useRef(eventPerfStart({ type: 'attendee', name: 'join.button' }));

  const [disableControlsHover, setDisableControlsHover] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [joinMessage, setJoinMessage] = React.useState<string | null>(null);

  // Track buffering of video presentation
  const [isBuffering, setIsBuffering] = React.useState(false);
  const bufferPerfRef = React.useRef({});
  const seekingFromRef = React.useRef(0);

  const [isSeeking, setIsSeekingState] = React.useState(false);
  const isSeekingRef = React.useRef(false);
  const setIsSeeking = (seeking: boolean): void => {
    setIsSeekingState(seeking);
    isSeekingRef.current = seeking;
  };

  const lastActiveDateRef = React.useRef(new EDate(serverDateNow(), 'UTC'));
  // This function is called when the attendee Chat or submit an Interaction
  const markUserActive = React.useCallback((): void => {
    lastActiveDateRef.current = new EDate(serverDateNow(), 'UTC');
  }, [serverDateNow]);
  const [inactiveReplay, setInactiveReplay] = React.useState(false);
  const onInactiveReplay = React.useCallback((): void => {
    setInactiveReplay(true);
  }, []);
  const onRejoinReplay = React.useCallback((): void => {
    setInactiveReplay(false);
    markUserActive();
  }, [markUserActive]);

  const [activeInteractionId, setActiveInteractionId] = React.useState<string | undefined>();
  const activeInteractionRef = React.useRef<ActiveInteraction>();
  const markInteractionInactive = React.useCallback((interaction?: InteractionInfo): void => {
    if (!interaction || activeInteractionRef.current?.id === interaction.id) {
      setActiveInteractionId(undefined);
      activeInteractionRef.current = undefined;
    }
  }, []);

  const markInteractionActive = React.useCallback(
    (interaction: InteractionInfo): void => {
      activeInteractionRef.current = {
        id: interaction.id!,
        lastActive: new EDate(serverDateNow(), LocalTimeZone.name),
      };
      setActiveInteractionId(interaction.id);

      logger.debug(
        'ANIM',
        'activeInteractionRef',
        activeInteractionRef.current?.lastActive.toISOString()
      );

      markUserActive();

      // DD: I don't think we want this here.  Scroll is a separate action
      // scrollToCardOrPosition(interaction);
    },
    [markUserActive, serverDateNow]
  );

  const hasAgenda = React.useMemo(() => {
    if (!hasJoined) return false;
    return (
      interactions.findIndex(
        (i) => i.type === InteractionType.Anchor && i.details?.anchor?.includeInAgenda
      ) !== -1
    );
  }, [interactions, hasJoined]);

  // This flag indicates the Browser is in background or foreground
  const [isInBackground, setIsInBackground] = React.useState(true);

  React.useEffect(() => {
    if ([PlayerMode.live, PlayerMode.replay].includes(mode) && !vimeoVideoId) {
      logger.error('There is no video in published webinar', {
        setId: set.id,
        ewebinar: ewebinar.id,
      });
    }
  }, [ewebinar.id, mode, set.id, vimeoVideoId]);

  const [errorCode, setErrorCode] = React.useState<number | undefined>();
  const [videoResponseQuery, videoResponseQueryResult] = useWebinarVideoResponseLazyQuery();
  // Only detect Vimeo video error when having error on the frontend
  React.useEffect(() => {
    if (!blocked) {
      return;
    }
    videoResponseQuery({
      variables: {
        ewebinarId: ewebinar.id,
      },
    });
  }, [blocked, ewebinar.id, videoResponseQuery]);
  React.useEffect(() => {
    if (!videoResponseQueryResult.data?.webinarVideoResponse) return;
    const response = videoResponseQueryResult.data.webinarVideoResponse;
    if ([403, 404].includes(response.statusCode)) {
      setErrorCode(response.statusCode);
    }
  }, [videoResponseQueryResult.data?.webinarVideoResponse]);

  const playerSeekNow = React.useCallback(
    async (position: number): Promise<void> => {
      if (!player) return;

      position = Math.max(0, Math.min(position, vimeoDuration));
      logger.debug('PLAY', '199  SEEK AFTER DEBOUNCE TO ', position);

      try {
        logger.debug('PLAY', '381 SETCURRENTTIME ', position);
        await player.setCurrentTime(position);
      } catch (e) {
        logger.debug('PLAY', '204 EXCEPTION', { position, vimeoDuration });
      }
    },
    [player, vimeoDuration]
  );

  const playerSeekDebounced = useDebouncedCallback(playerSeekNow, 100, {
    leading: false,
    trailing: true,
  });

  const pausePlayer = React.useCallback(
    (noSkip?: boolean): void => {
      if (mode === PlayerMode.live && hasJoined !== HasJoined.Joined && !noSkip) {
        // Skip pause until we fully join after playback starts
        logger.debug('PLAY', '523 SKIP PAUSE', { mode, hasJoined });
        return;
      }

      // Sometimes seeking fails - reset it
      logger.debug('PLAY', '485 SET SEEKING FALSE');
      setIsSeeking(false);
      player?.pause();
    },
    [player, mode, hasJoined]
  );

  const setPlaybackPositionStateAndRef = React.useCallback(
    (pos: number): void => {
      const newRoom = rooms.getRoomAt(pos);
      if (mode === PlayerMode.replay && newRoom.type === RoomType.Exit) {
        // Cap replay position inside presentation room
        pos = Math.floor(vimeoDuration) - 0.01;
      }

      playbackPositionValue.set(pos);
      playbackPositionRef.current = Math.floor(pos);
      setPlaybackPositionState(pos);
    },
    [mode, playbackPositionValue, rooms, vimeoDuration]
  );

  const setPlaybackPosition = React.useCallback(
    async (newPosition: number, seekBeforePlay?: boolean): Promise<void> => {
      const newRoom = rooms.getRoomAt(newPosition);
      const currentPosition = playbackPositionRef.current;
      const currentRoom = rooms.getRoomAt(currentPosition);

      if (blockSeekAndPlayRef.current.type !== 'none') {
        const blockedPosition = blockSeekAndPlayRef.current?.atPosition ?? 0;
        if (
          blockSeekAndPlayRef.current.type === 'all' ||
          (blockSeekAndPlayRef.current.type === 'forward' && newPosition > blockedPosition) ||
          (blockSeekAndPlayRef.current.type === 'backward' && newPosition < blockedPosition)
        ) {
          logger.debug('PLAY', 'BLOCKED SETPLAYBACKPOS');
          return;
        }
      }

      if (newRoom.type !== RoomType.Presentation) {
        setPlaybackPositionStateAndRef(newPosition);
        return;
      }

      if (!player && mode === PlayerMode.edit) {
        // No player means no video while Editing - set position anyway so
        // we cursor through the Presentation area
        setPlaybackPositionStateAndRef(newPosition);
        return;
      }

      if (newPosition < 0 || newPosition > vimeoDuration) {
        // When joining in Exit room I was seeing buffering spinner and
        // hearing the video play behind the Exit room on re-hydration
        logger.debug('PLAY', '196 SAFE SKIPPED ', [player, newPosition]);
        logger.debug('PLAY', '253 PAUSE!');
        pausePlayer();
      }

      if (!player) {
        setPlaybackPositionStateAndRef(newPosition);
        seekingFromRef.current = currentPosition;
        setIsSeeking(true);
        return;
      }

      if (
        isPlaying &&
        currentRoom.type === RoomType.Exit &&
        newRoom.type === RoomType.Presentation // Join in Exit Room
      ) {
        // From the exit room, the player maybe not have played yet, calling play() with user interaction
        player.play();
      }

      const time = await player.getCurrentTime();
      if (Math.floor(time) === Math.floor(newPosition)) {
        // Don't seek to same time we're already at
        logger.debug('PLAY', '467 NO SEEK NEEDED');
        setPlaybackPositionStateAndRef(newPosition);
        return;
      }

      if (hasJoined !== HasJoined.Joined) {
        setPlaybackPositionStateAndRef(newPosition);
        return;
      }

      const currentTime = await player.getCurrentTime();
      if (Math.abs(currentTime - newPosition) < 1 && newRoom.type === currentRoom.type) {
        logger.debug('PLAY', '521 CURRENT - NEW < 1: NO SEEK', {
          currentTime,
          position: newPosition,
        });
        setPlaybackPositionStateAndRef(newPosition);
        return;
      }
      logger.debug('PLAY', '410 PLAYER TIME != POSITION', { currentTime, position: newPosition });

      try {
        logger.debug('PLAY', '459 START SEEK!! >> ', currentPosition, ' -> ', newPosition);

        seekingFromRef.current = currentPosition;
        setIsSeeking(true);
        setPlaybackPositionStateAndRef(newPosition);

        if (seekBeforePlay || newRoom.type !== currentRoom.type) {
          playerSeekNow(newPosition);
        } else {
          playerSeekDebounced(newPosition);
        }
      } catch (e) {
        const error = e as Error;

        logger.debug('PLAY', 'SEEK ERROR.  SET SEEKING FALSE ', { error });
        setIsSeeking(false);
        ErrorReport.notify(error);
      }
    },
    [
      hasJoined,
      mode,
      pausePlayer,
      player,
      playerSeekDebounced,
      playerSeekNow,
      rooms,
      setPlaybackPositionStateAndRef,
      vimeoDuration,
      isPlaying,
    ]
  );

  const recalcCurrentPlaybackPosition = React.useCallback(
    (attendeeChanged?: boolean): number => {
      if (isAnonymousAttendee) return playbackPositionRef.current;

      if (
        !attendee ||
        !attendee.startTime ||
        isSeekingRef.current ||
        (attendee.isOnDemand && !attendee.joinTime)
      )
        return playbackPositionRef.current;

      // Fallback calculation assuming we have no segments which means calcs are
      // super basic and speed === 1
      let newPlaybackPosition = Math.ceil(
        (serverDateNow().getTime() - new Date(attendee.startTime).getTime()) / 1000
      );

      if (lastSegmentRef.current) {
        [newPlaybackPosition] = calcPlaybackFromLastSegment(lastSegmentRef.current);
      }

      // update new playbackPosition when attendee click anchor to jump from exit room to presentation room
      if (
        currentRoom.type === RoomType.Presentation &&
        newPlaybackPosition > vimeoDuration &&
        playbackPosition <= vimeoDuration &&
        !attendeeChanged
      ) {
        newPlaybackPosition = playbackPosition;
        logger.debug('PLAY', '534 START SEEK!! >> ', newPlaybackPosition);
      }

      logger.debug('PLAY', '533 RECALC CURRENT POSITION ', {
        isSeeking: isSeekingRef.current,
        newPlaybackPosition,
        lastSegment: {
          ...lastSegmentRef.current,
        },
      });

      setPlaybackPositionStateAndRef(newPlaybackPosition);
      return newPlaybackPosition;
    },
    [
      attendee,
      isAnonymousAttendee,
      serverDateNow,
      currentRoom.type,
      vimeoDuration,
      playbackPosition,
      setPlaybackPositionStateAndRef,
      calcPlaybackFromLastSegment,
    ]
  );

  const lazyRecalcAndSeek = React.useCallback(
    async (
      playAfterSeek?: boolean,
      acceptableDeltaSecs = 7,
      attendeeChanged?: boolean
    ): Promise<void> => {
      if (isAnonymousAttendee) return;
      if (mode !== PlayerMode.live) {
        return;
      }

      if (blockSeekAndPlayRef.current.type && blockSeekAndPlayRef.current.type !== 'none') {
        logger.debug('PLAY', 'BLOCKED RECALC SEEK');
        return;
      }

      const position = recalcCurrentPlaybackPosition(attendeeChanged);
      logger.debug('PLAY', '632 RECALC FROM LAZY', {
        playAfterSeek,
        acceptableDeltaSecs,
        position,
      });

      if (
        player &&
        loaded &&
        currentRoom.type === RoomType.Presentation &&
        /* playAfterSeek is used when joining but not quite joined yet */
        (hasJoined === HasJoined.Joined || playAfterSeek)
      ) {
        const currentTime = await player.getCurrentTime();

        if (Math.abs(position - currentTime) > acceptableDeltaSecs) {
          if (position >= 0 && position <= vimeoDuration) {
            logger.debug('PLAY', `279 RECALC SHOULD BE AT ${position} SEEKING FROM `, currentTime);

            if (playAfterSeek) {
              setWillPlayAfterSeek(true);
              pausePlayer();
            }

            seekingFromRef.current = playbackPositionRef.current;
            setIsSeeking(true);

            try {
              logger.debug(
                'PLAY',
                '622 SETCURRENTTIME ',
                Math.min(position, vimeoDuration - 0.001)
              );
              await player.setCurrentTime(Math.max(0, Math.min(position, vimeoDuration - 0.001)));
            } catch (e) {
              logger.debug('PLAY', '204 EXCEPTION', { position, vimeoDuration });
            }
          } else {
            logger.debug('PLAY', '261 RECALC PAUSE PAUSE');
            pausePlayer(attendeeChanged);
          }
        } else {
          if (position < 0 || position > vimeoDuration) {
            logger.debug('PLAY', '342 RECALC PAUSE');
            pausePlayer();
          }
          logger.debug('PLAY', '504 RECALC SEEK SKIP (DELTA TOO SMALL):', position - currentTime);
        }
      }

      return;
    },
    [
      isAnonymousAttendee,
      currentRoom.type,
      hasJoined,
      loaded,
      mode,
      pausePlayer,
      player,
      recalcCurrentPlaybackPosition,
      vimeoDuration,
    ]
  );

  // Check room to hide volume icon in waiting room and show it when in presentation room
  const onSetVolumeWhenPlaying = React.useCallback((): void => {
    if (!player) return;

    if (currentRoom && currentRoom.type === RoomType.Waiting && isPlaying) {
      logger.debug('PLAY', '362, VOL: 0');
      player.setVolume(0);
    }

    if (
      currentRoom &&
      currentRoom.type === RoomType.Presentation &&
      isPlaying &&
      hasJoined === HasJoined.Joined
    ) {
      if (muted) {
        player.setVolume(0);
      } else {
        logger.debug('PLAY', `372, VOL: ${volume}`);
        player.setVolume(volume);
      }
    }
  }, [currentRoom, hasJoined, isPlaying, muted, player, volume]);

  const { join } = useAttendeeContext();
  const handleJoinPlaySuccess = React.useCallback(
    async (attendeeChanged?: boolean): Promise<void> => {
      try {
        const joinWebinarPerf = eventPerfStart({ type: 'attendee', name: 'join.webinar' });

        let now = serverDateNow().toISOString();

        if (!attendee?.joinTime && attendee?.isOnDemand) {
          now = new EDate(serverDateNow(), 'UTC').toDate().toISOString();

          setLastSegment({
            startTime: now,
            startAtSecs: 0,
            speed: 1,
          });
          logger.debug('PLAY', 'SETTING LASTSEGMENT TO ONDEMAND ', { ...lastSegmentRef.current });

          if (attendee) {
            attendee.startTime = now;
          }
        }

        const isPlaying =
          !pauseTimer && mode === PlayerMode.live && blockSeekAndPlayRef.current.type === 'none';

        if (!isPlaying) {
          logger.debug('PLAY', '1024 SET ISPLAYING FALSE - PAUSE TIMER');
          setIsPlaying(false);
        } else {
          // If we're live start playback
          logger.debug('PLAY', '1030 SET ISPLAYING ', true);
          setIsPlaying(true);
        }

        if (!isPlaying || currentRoom.type !== RoomType.Presentation) {
          logger.debug('PLAY', '377 JOIN - PAUSE AFTER PLAY');
          await player?.pause();
        }

        if (!attendeeChanged) {
          logger.debug('PLAY', '317 JOIN LAZY RECALC TO 1 SECOND');
          await lazyRecalcAndSeek(isPlayingRef.current, 1, attendeeChanged);

          // Check room to set volume in waiting room and presentation room
          onSetVolumeWhenPlaying();
        }

        setError(null);
        setJoinMessage(null);

        // Save current time as Join time
        await join({
          joinTime: now,
          connectionId: connectionId ? connectionId : shortIdRef.current,
        });

        setJoinTime(new Date(now));
        logger.debug('PLAY', '403 HAS JOINED');
        eventPerfEnd(joinWebinarPerf);
        setHasJoined(HasJoined.Joined);
        setError(null);
        if (attendeeChanged) {
          logger.debug('PLAY', '317 JOIN LAZY RECALC TO 1 SECOND');
          await lazyRecalcAndSeek(isPlayingRef.current, 1, attendeeChanged);

          // Check room to set volume in waiting room and presentation room
          onSetVolumeWhenPlaying();
        }
      } catch (e) {
        const error = e as Error;
        console.error('ERROR POST JOIN: ', { error });

        ErrorReport.notify(error);
        setHasJoined(HasJoined.NotJoined);
      }
    },
    [
      player,
      serverDateNow,
      attendee,
      pauseTimer,
      mode,
      currentRoom.type,
      lazyRecalcAndSeek,
      onSetVolumeWhenPlaying,
      connectionId,
      setLastSegment,
      join,
      isPlayingRef,
    ]
  );
  const [shouldCallJoinPlaySuccess, setShouldCallJoinPlaySuccess] = React.useState(false);
  const shouldCallJoinPlaySuccessChanged = useHasChangedShallow([shouldCallJoinPlaySuccess]);
  const attendeeIdChanged = useHasChangedShallow([attendee?.id]);
  React.useEffect(() => {
    if (
      (!shouldCallJoinPlaySuccessChanged && !attendeeIdChanged) ||
      !shouldCallJoinPlaySuccess ||
      isAnonymousAttendee
    ) {
      return;
    }
    const lastSegment =
      mode === PlayerMode.replay ? attendee?.lastReplaySegment : attendee?.lastWatchedSegment;
    if (lastSegment) {
      lastSegmentRef.current = lastSegment;
    }
    handleJoinPlaySuccess(attendeeIdChanged);
  }, [
    handleJoinPlaySuccess,
    attendee,
    isAnonymousAttendee,
    shouldCallJoinPlaySuccess,
    shouldCallJoinPlaySuccessChanged,
    mode,
    setLastSegment,
    attendeeIdChanged,
  ]);
  const joinPlaySuccess = React.useCallback(async (): Promise<void> => {
    if (!player) return;
    player.off('timeupdate', joinPlaySuccess);
    setShouldCallJoinPlaySuccess(true);
  }, [player]);

  const joinPlayError = React.useCallback(
    (error: Error): void => {
      if (!player) return;
      player.off('timeupdate', joinPlaySuccess);

      logger.error('430 JOIN PLAY ERROR: ', error);
      setHasJoined(HasJoined.NotJoined);
      ErrorReport.notify(error);
    },
    [joinPlaySuccess, player]
  );

  const clickedJoin = React.useCallback((): void => {
    const player = playerRef.current;
    // Needs to be called as a result of user interaction
    if (!player || !loaded) {
      logger.error('ERROR IN JOIN: clickedJoin called before loading complete', [player, loaded]);
      return;
    }

    setHasJoined(HasJoined.Joining);

    logger.debug('PLAY', '475 JOIN VOL 0');
    player.setVolume(0);

    // triggering play when video is not loaded, it fails silently
    logger.debug('PLAY', '437 JOIN PLAY >');
    player.on('timeupdate', joinPlaySuccess);
    player.play().catch(joinPlayError);
  }, [joinPlayError, joinPlaySuccess, loaded, playerRef]);

  const hasPlayerChanged = useHasChangedShallow([player]);
  React.useEffect(() => {
    if (!hasPlayerChanged || !player) return;

    const onLoaded = async (): Promise<void> => {
      setError(null);
      setLoaded(true);
      logger.debug('PLAY', '209 LOADED');
      eventPerfEnd(joinButtonPerfRef.current);

      // Check to hide icon volume in waiting room
      onSetVolumeWhenPlaying();

      if (isSeekingRef.current) {
        // Seeking from the exit room, no need to recalc the playback position
        setIsSeeking(false);
        return;
      }

      logger.debug('PLAY', '357 RECALC SEEK');
      lazyRecalcAndSeek();

      // Seeking here makes initial frame seen the one we want
      // Shift by 0.00xf so fromSeek and toSeek aren't equal later in the useEffect
      let initialVideoTime: number | undefined = undefined;
      switch (mode) {
        case PlayerMode.edit:
          // Hack to show inital frame
          initialVideoTime = playbackPositionRef.current + 0.1;
          break;

        case PlayerMode.replay:
          if (lastSegmentRef.current?.endAtSecs) {
            initialVideoTime = lastSegmentRef.current.endAtSecs - 0.001;
          }
          break;

        case PlayerMode.live:
          if (lastSegmentRef.current && !(attendee?.isOnDemand && !attendee.joinTime)) {
            [initialVideoTime] = calcPlaybackFromLastSegment(lastSegmentRef.current);
            initialVideoTime -= 0.001;
          }
      }
      if (initialVideoTime !== undefined) {
        initialVideoTime = Math.min(initialVideoTime, vimeoDuration);

        logger.debug('PLAY', '764 SET PLAYER TIME ', { vimeoDuration, initialVideoTime });
        player.setCurrentTime(Math.max(0, Math.min(initialVideoTime, vimeoDuration - 0.001)));
      }

      const getVideoDuration = async (): Promise<void> => {
        const duration = await player.getDuration();
        logger.debug('PLAY', '1019 Real vimeo duration: ', duration);

        if (duration !== ewebinar?.duration) {
          setVimeoDuration(duration);
        }
      };
      // Get video length down to the millisecond
      await getVideoDuration();
    };

    const onError = (error: PlayerError): void => {
      console.error('PLAYER ONERROR: ', JSON.stringify(error));

      if (mode === PlayerMode.live && error.method === 'play') {
        // Only check autoplay permission for Live mode
        switch (error.name) {
          case 'PlayInterrupted':
            // No error to report
            break;

          case 'NotAllowedError':
            setError(
              t`Error playing video: Please make sure your browser and browser plugins allow auto-playing video from this site.`
            );
            break;

          default:
            if (hasJoined !== HasJoined.Joined) {
              setError(
                t`Error playing video: Please make sure your browser and browser plugins allow auto-playing video from this site.` +
                  ` Error: ${error.message}`
              );
            }
        }
      }

      ErrorReport.notify(error);
    };

    const waitForPlayer = async (): Promise<void> => {
      try {
        await player.ready();
        logger.debug('PLAY', '738 Player ready');

        // Hack: make sure player really is loaded on mobile.
        // For the issue play / pause be loop forever in the replay mode if the player is not really loaded
        onLoaded();
      } catch (r) {
        const reason = r as Error;

        console.error('PLAYER !READY: ', reason);
        setError(t`Error connecting to video`);
        ErrorReport.notify(reason);
      }
    };

    player.on('error', onError);
    void waitForPlayer();

    return (): void => {
      player?.off('error', onError);
    };
  }, [
    lazyRecalcAndSeek,
    mode,
    onSetVolumeWhenPlaying,
    player,
    hasPlayerChanged,
    ewebinar?.duration,
    pauseTimer,
    vimeoDuration,
    calcPlaybackFromLastSegment,
    hasJoined,
    attendee?.isOnDemand,
    attendee?.joinTime,
  ]);

  const hasRoomChanged = useHasChangedShallow([currentRoom]);
  React.useEffect(() => {
    if (!hasRoomChanged) {
      return;
    }

    // Current room has changed
    logger.debug('PLAY', `989 NEW ROOM: ${currentRoom.type}`);

    if (currentRoom.type !== RoomType.Presentation) {
      // Force player pause outside presentation room
      logger.debug('PLAY', `398 ROOM ${currentRoom.type} PAUSE PLAYER ||`);
      pausePlayer();
    }

    if (currentRoom.type === RoomType.Waiting) {
      // Prep video for playing from start again
      logger.debug('PLAY', '442 PLAYER SEEK @ 0');
      pausePlayer();
    }

    if (mode === PlayerMode.live && currentRoom.type === RoomType.Exit) {
      // In case person is joining after video
      setHasJoined(HasJoined.Joined);
      // Set playback speed to 1 as that's the only speed supported in exit room
      // and we want a new lastSegment to indicate speed = 1
      setPlaybackSpeed(1);

      // Make sure we start 1s intervals if we join in exit room
      logger.debug('PLAY', '1487 SET ISPLAYING TRUE');
      setIsPlaying(true);
    }

    if (mode === PlayerMode.replay && currentRoom.type === RoomType.Exit) {
      // Pause at end of replay
      logger.debug('PLAY', '1026 REPLAY END OF PRESENTATION - ISPLAYING FALSE');
      setIsPlaying(false);
    }

    if (currentRoom.type === RoomType.PostExit) {
      logger.debug('PLAY', '1031 POSTEXIT - Actions Pane GONE');
      setActionsPaneState('offscreen');
    }
  }, [
    attendee,
    currentRoom,
    hasRoomChanged,
    mode,
    pausePlayer,
    setActionsPaneState,
    setPlaybackSpeed,
  ]);

  React.useEffect(() => {
    // Only matters for live playback
    if (mode !== PlayerMode.live) return;

    const onVisibilityChange = (): void => {
      if (window.document.visibilityState === 'visible') {
        // when app comes from background to foreground
        // recalculate video position

        logger.debug('PLAY', '920 NO LONGER IN BG - RECALC SEEK');
        setIsInBackground(false);
        lazyRecalcAndSeek();
      } else {
        logger.debug('PLAY', '920 IN BG - RECALC SEEK');
        setIsInBackground(true);
      }
    };

    if (window) {
      // If the window has been in background too long video may be out of place
      window.document.addEventListener('visibilitychange', onVisibilityChange);
    }
    return (): void => {
      window.document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [rooms, player, loaded, mode, hasJoined, lazyRecalcAndSeek]);

  const incrementPlaybackPosition = React.useCallback((): void => {
    if (
      !isPlaying ||
      (currentRoom.type === RoomType.Presentation && !isLastSecondOfPresentationRoom)
    )
      return;

    // Calculate playbackPosition based on Now - Last segment
    let newPosition = Math.ceil(playbackPositionRef.current + 1);
    if (lastSegmentRef.current) {
      [newPosition] = calcPlaybackFromLastSegment(lastSegmentRef.current);
    }

    if (
      mode === PlayerMode.edit &&
      newPosition > ewebinar.duration + ewebinar.exitRoomDurationSecs
    ) {
      // Reached the end - don't update playbackPosition
      logger.debug('PLAY', '883 END OF ROOMS - ISPLAYING - PAUSE');
      setIsPlaying(false);
      return;
    }
    setPlaybackPosition(newPosition);
  }, [
    isPlaying,
    currentRoom.type,
    mode,
    ewebinar.duration,
    ewebinar.exitRoomDurationSecs,
    setPlaybackPosition,
    calcPlaybackFromLastSegment,
    isLastSecondOfPresentationRoom,
  ]);

  const [playbackEvent] = usePlaybackEventMutation();

  const stillPlaying = React.useCallback(async (): Promise<void> => {
    try {
      if (!attendee?.id) return;

      await playbackEvent({
        variables: {
          id: attendee.id,
          connectionId: connectionId ? connectionId : shortIdRef.current,
          forReplay: false,
          events: [
            {
              type: PlaybackEventType.StillPlaying,
              atDate: serverDateNow().toISOString(),
              atSecs: playbackPositionRef.current,
            },
          ],
        },
      });
    } catch (error) {
      logger.error('still playing heartbeat call failed', {
        error,
        attendeeId: attendee?.id,
      });
    }
  }, [attendee?.id, connectionId, playbackEvent, serverDateNow]);

  const interval = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    // Track playbackPosition based on 1 second interval while NOT in presentation room

    if (
      !isPlaying ||
      isSeeking || // If seeking let that happen first before we increment anything
      (currentRoom.type === RoomType.Presentation &&
        player && // If !player there's no video - keep counting time here
        !isLastSecondOfPresentationRoom) // Sometime the real video stopped before the duration and the onEnded event doesn't fire, so we setup the interval before the end 1s to keep counting time ||
    ) {
      logger.debug('PLAY', '1598 NO EXIT ROOM 1s INTERVAL', { isPlaying });

      // Don't setup interval.  Track playbackPosition based on player progress
      return;
    }

    logger.debug('PLAY', '877 SETTING UP 1s INTERVAL');

    interval.current = setInterval(incrementPlaybackPosition, 1000);
    return (): void => {
      logger.debug('PLAY', '881 REMOVING 1s INTERVAL!!!');
      interval.current && clearInterval(interval.current);
    };
  }, [
    currentRoom,
    currentRoom.type,
    isPlaying,
    isSeeking,
    player,
    incrementPlaybackPosition,
    isLastSecondOfPresentationRoom,
  ]);

  React.useEffect(() => {
    // Track playbackPosition based on vimeo player progress while in presentation room
    if (!player) return;

    const onTimeUpdate: EventCallback = async ({ seconds, duration }) => {
      playerCurrentTimeRef.current = seconds;

      if (duration !== vimeoDuration) {
        // Update duration to actual Microsecond accurate duration
        logger.debug('PLAY', '1234 Real vimeo duration: ', vimeoDuration, duration);
        setVimeoDuration(duration);
      }

      if (isSeekingRef.current) {
        logger.debug('PLAY', '705 SKIP TIMEUPDATE (SEEKING)', { seconds });
        return;
      }
      if (!isPlaying) {
        logger.debug('PLAY', '705 SKIP TIMEUPDATE (!ISPLAYING)', { seconds });
        pausePlayer();
        return;
      }

      if (
        playbackPositionRef.current < 0 ||
        playbackPositionRef.current >= vimeoDuration ||
        currentRoom.type !== RoomType.Presentation
      ) {
        logger.debug('PLAY', '705 SKIP TIMEUPDATE (!PRESENTATION ROOM)', { seconds });
        return;
      }
      markUserActive();

      if (Math.abs(seconds - playbackPositionRef.current) > 10) {
        logger.debug('PLAY', '710 SKIP TIMEUPDATE (TIMEUPDATE IS OFF)', {
          seconds,
          playbackPosition: playbackPositionRef.current,
        });

        seekingFromRef.current = playbackPositionRef.current;
        setIsSeeking(true);

        try {
          logger.debug('PLAY', '1114 SETCURRENTTIME ', playbackPositionRef.current);
          await player.setCurrentTime(playbackPositionRef.current);
        } catch (e) {
          logger.debug('PLAY', '204 EXCEPTION', { playbackPosition: playbackPositionRef.current });
        }
        return;
      }

      const secs: number = Math.floor(seconds);
      if (playbackPositionRef.current !== secs) {
        setPlaybackPositionStateAndRef(secs);
        sendPlaybackDataToCypress({ seconds: secs });
        return;
      }
    };

    player.on('timeupdate', onTimeUpdate);
    return (): void => {
      player.off('timeupdate', onTimeUpdate);
    };
  }, [
    currentRoom.type,
    isPlaying,
    markUserActive,
    pausePlayer,
    player,
    setPlaybackPositionStateAndRef,
    vimeoDuration,
  ]);

  // Sync muted state with player - not volume
  const hasMuteChanged = useHasChangedShallow([muted, player, hasJoined, isPlaying]);

  React.useEffect(() => {
    async function onSetMuted(): Promise<void> {
      if (!hasMuteChanged || !player || hasJoined !== HasJoined.Joined) return;

      if (muted) {
        logger.debug('PLAY', '384,VOL: 0');
        player?.setVolume(0);
      } else {
        if (!isPlaying) {
          logger.debug('PLAY', '368, VOL: 0');
          player?.setVolume(0);
        } else {
          if (volume == 0) {
            setVolume(1);
            logger.debug('PLAY', '393,VOL: 1  PLAY ', isPlaying);
            player?.setVolume(1);
          } else {
            logger.debug('PLAY', '396,VOL: ', volume, '  PLAY ', isPlaying);
            player?.setVolume(volume);
          }
          setMutedByJoiningIssue(false);
        }
      }
    }

    onSetMuted();
  }, [muted, player, isSeeking, isPlaying, hasJoined, hasMuteChanged, volume]);

  React.useEffect(() => {
    if (!player) return;

    const onBufferStart = (): void => {
      logger.debug('PLAY', 'BUFFERING...');
      setIsBuffering(true);
      bufferPerfRef.current = eventPerfStart({
        type: 'video',
        name: 'buffer.duration',
        aid: attendee?.id,
        pos: playbackPositionRef.current,
      });
    };

    const onBufferEnd = (): void => {
      logger.debug('PLAY', 'BUFFERED!');
      setIsBuffering(false);
      eventPerfEnd(bufferPerfRef.current as EventPerfDetails);
    };

    const onSeeked = async (): Promise<void> => {
      logger.debug('PLAY', '1362 ONSEEKED: SET SEEKING FALSE');
      setIsSeeking(false);

      const time = await player.getCurrentTime();
      logger.debug('PLAY', '433 SEEKED  @ ', time);
    };

    let timer: NodeJS.Timeout;
    const onEnded = async (): Promise<void> => {
      if (isSeekingRef.current) {
        // Currently seeking so onEnded is premature - ignore
        return;
      }

      // In replay we don't force the jump to the next room
      if (mode !== PlayerMode.replay) {
        const room = rooms.getRoom(RoomType.Presentation);

        const currentRoom = rooms.getRoomAt(playbackPositionRef.current);
        if (currentRoom.type === RoomType.Presentation) {
          // Only if playbackPosition shows us still in Presentation room (i.e. we're at 136.2 and duration is 137 then
          // wait a few milliseconds and force us out of Presentation room

          const waitDuration = 1000 - (room.duration - Math.floor(room.duration)) * 1000;
          const nextPlaybackPosition = Math.floor(room.duration + 1);

          logger.debug(
            'PLAY',
            `1142 WAIT FOR ${waitDuration}ms THEN JUMP TO ${nextPlaybackPosition}`
          );
          timer = setTimeout(() => setPlaybackPosition(nextPlaybackPosition), waitDuration);
        }

        // Send StillPlaying event when the video ended to calc duration
        // don't send in Replay because we send a Paused event when a replay ended
        await stillPlaying();
      } else {
        logger.debug(
          'PLAY',
          'FORCE REPLAY END TO PRESENATION ROOM ISPLAYING @ ',
          Math.floor(vimeoDuration)
        );
        setIsPlaying(false);
        setPlaybackPosition(Math.floor(vimeoDuration));
      }
    };

    player.on('bufferstart', onBufferStart);
    player.on('bufferend', onBufferEnd);
    player.on('seeked', onSeeked);
    player.on('ended', onEnded);

    return (): void => {
      player.off('bufferstart', onBufferStart);
      player.off('bufferend', onBufferEnd);
      player.off('seeked', onSeeked);
      player.off('ended', onEnded);
      timer && clearTimeout(timer);
    };
  }, [player, mode, vimeoDuration, setPlaybackPosition, rooms, stillPlaying, attendee?.id]);

  const hasPlayerLoadedChanged = useHasChangedShallow([loaded]);
  const lastReplayPosition = lastSegmentRef.current?.endAtSecs ?? 0;
  React.useEffect(() => {
    if (
      !hasPlayerLoadedChanged ||
      !player ||
      !loaded ||
      !attendee ||
      !watchingReplay ||
      lastReplayPosition === 0
    ) {
      return;
    }

    // Run once when player is ready
    const position = Math.min(lastReplayPosition, (vimeoDuration ?? ewebinar.duration) - 0.001);
    logger.debug('PLAY', '1011 SET INITIAL POS @ ', position);
    setPlaybackPosition(position);
  }, [
    attendee,
    ewebinar.duration,
    hasPlayerLoadedChanged,
    lastReplayPosition,
    loaded,
    player,
    setPlaybackPosition,
    vimeoDuration,
    watchingReplay,
  ]);

  const hasIsSeekingChanged = useHasChangedShallow([isSeeking]);
  React.useEffect(() => {
    if (!hasIsSeekingChanged || !player) return;

    if (!isSeeking && willPlayAfterSeek) {
      logger.debug('PLAY', '572 ISPLAYING TRUE');
      setWillPlayAfterSeek(false);
      setIsPlaying(true);
    }
  }, [player, isSeeking, willPlayAfterSeek, hasIsSeekingChanged]);

  // Handle room change
  const hasRoomOrJoinedChanged = useHasChangedShallow([
    currentRoom.type,
    player,
    hasJoined,
    isSeeking,
  ]);
  React.useEffect(() => {
    if (!hasRoomOrJoinedChanged || !player || hasJoined !== HasJoined.Joined || isSeeking) {
      return;
    }

    // Set volume when in presentation room
    onSetVolumeWhenPlaying();

    if (isPlaying && currentRoom.type === RoomType.Presentation) {
      // Ensure vimeo player is paused when isPlaying is false
      logger.debug('PLAY', `2018 ROOM ${currentRoom.type} PLAY >`);

      player
        .play()
        .then((s) => {
          logger.debug('PLAY', `2023 PLAYED`, s);
        })
        .catch((e) => {
          const error = e as Error;
          logger.error(`2017 PLAY ERROR ${error.message}`);
        });

      setTimeout(() => {
        player.play();
      }, 1000);

      logger.debug('PLAY', `2023 PLAYED`);
    }
  }, [
    currentRoom.type,
    isSeeking,
    player,
    hasJoined,
    hasRoomOrJoinedChanged,
    onSetVolumeWhenPlaying,
    isPlaying,
    pausePlayer,
  ]);

  const hasSeekingChanged = useHasChangedShallow([isSeeking, loaded, player]);
  // Replay syncing
  React.useEffect(() => {
    if (!hasSeekingChanged || !player || !loaded || mode !== PlayerMode.replay) {
      return undefined;
    }

    const syncPlay = (): void => {
      if (!isSeeking) {
        // Unclear why but when we grab and drag the green bar
        // in edit mode, this function gets called to start play
        // so skip if we're actively seeking
        return;
      }

      logger.debug('PLAY', '619 ISPLAYING TRUE');
      setIsPlaying(true);
    };

    const syncPause = ({ duration, seconds }: { duration: number; seconds: number }): void => {
      // if the player paused at the end, skip this
      if (duration !== seconds) {
        logger.debug('PLAY', '1190 ISPLAYING FALSE');
        setIsPlaying(false);
      }
    };

    // Handle hardware buttons
    player.on('play', syncPlay);
    player.on('pause', syncPause);

    return (): void => {
      player.off('play', syncPlay);
      player.off('pause', syncPause);
    };
  }, [hasSeekingChanged, isSeeking, loaded, mode, player]);

  React.useEffect(() => {
    // This effect forces playback of the video stream even after the user
    // pauses it with a hardware button.  However, this can lead to an infinite
    // loop when it's the device that's forcing the pause (e.g. when the user
    // is in the background on Mobile).
    const player = playerRef.current;

    if (!player || !loaded || mode !== PlayerMode.live) {
      return;
    }

    let lastTimePlayed = 0;
    let attempted = 0;
    let waitForRetrying = false;

    const play = async (): Promise<void> => {
      try {
        if (waitForRetrying) return;
        if (playbackPositionRef.current >= vimeoDuration) {
          return;
        }

        if (new Date().getTime() - lastTimePlayed < 100) {
          if (attempted++ < 10) {
            waitForRetrying = true;
            logger.error('2081 PLAYER PLAY retry', attempted);
            setTimeout(() => {
              waitForRetrying = false;
              play();
            }, 100);
            return;
          }

          // Give up if the pause happens immediately after play
          logger.debug(
            'PLAY',
            '2082 PLAYER PLAY Gave up re-playing on pause, try to play without sound'
          );
          await player.setMuted(true);
          player.play();

          let onTimeUpdate: (() => void) | undefined = undefined;
          let onPause: (() => void) | undefined = undefined;
          onPause = () => {
            logger.debug('PLAY', '2082-1 could not play, show error message with join button');
            setJoinMessage(t`Please try joining again...`);
            setHasJoined(HasJoined.NotJoined);
            player.off('timeupdate', onTimeUpdate);
            player.off('pause', onPause);
          };
          onTimeUpdate = () => {
            logger.debug('PLAY', '2082-2 play without sound, show unmute button');
            setMutedByJoiningIssue(true);
            setMuted(true);
            player.off('timeupdate', onTimeUpdate);
            player.off('pause', onPause);
          };
          player.on('timeupdate', onTimeUpdate);
          player.on('pause', onPause);
          return;
        }
        lastTimePlayed = new Date().getTime();

        // Android devices does not allow Playing in background
        if (isAndroid && isInBackground) return;

        if (await player?.getPaused()) {
          logger.debug('PLAY', '538 AUTO-PAUSED: PLAY > ');
          await player.play().catch((e) => {
            const error = e as Error;
            logger.error(`PLAY Error playing 538: ${error.message}`);
          });
        }
      } catch (e) {
        const error = e as Error;

        logger.debug('PLAY', '541 Error playing: ', { error });
        ErrorReport.notify(error);
      }
    };

    if (
      hasJoined === HasJoined.Joined &&
      currentRoom.type === RoomType.Presentation &&
      isPlaying &&
      !isLastSecondOfPresentationRoom
    ) {
      player.on('pause', play);

      return (): void => {
        player.off('pause', play);
      };
    }

    return;
  }, [
    currentRoom.type,
    hasJoined,
    isInBackground,
    isPlaying,
    loaded,
    mode,
    playerRef,
    vimeoDuration,
    isLastSecondOfPresentationRoom,
    setMutedByJoiningIssue,
    setMuted,
  ]);

  const hasPlayingChanged = useHasChangedShallow([isPlaying, currentRoom.type, player]);
  // If for some reason we start playing at the wrong time, pause
  React.useEffect(() => {
    // Don't check for loaded state - we can be playing BEFORE loaded
    if (!hasPlayingChanged || !player) {
      return;
    }

    const onPlay = async (): Promise<void> => {
      logger.debug('PLAY', '1362 SET SEEKING FALSE');
      setIsSeeking(false);

      if (isPlaying && currentRoom.type === RoomType.Presentation) {
        return;
      }

      logger.debug('PLAY', '868 FORCE PAUSE');
      pausePlayer();
    };
    player.on('play', onPlay);

    return (): void => {
      player.off('play', onPlay);
    };
  }, [currentRoom.type, hasPlayingChanged, isPlaying, pausePlayer, player]);

  /**
   * Show error if Video Player is blocked
   */
  React.useEffect(() => {
    if (!blocked) return;
    if (errorCode === 403) {
      setError(t`Because of its privacy settings, this video cannot be played here.`);
      return;
    }
    if (errorCode === 404) {
      setError(t`This video does not exist.`);
      return;
    }
    setError(
      t`Video playback is being blocked by a firewall or browser extension.` +
        ' ' +
        t`If you see a reCaptcha checkbox please click it now.`
    );
  }, [blocked, errorCode]);

  const domain = rooms?.domain;

  const duration = (watchingReplay ? rooms?.maxReplayDuration : rooms?.maxDuration) || 0;
  const setIsPlayingAPI: WebinarPlayerState['setIsPlaying'] = React.useCallback(
    (play, props): void => {
      logger.debug('PLAY', '2186 EXT ', play ? 'PLAY' : 'PAUSE');

      if (blockSeekAndPlayRef.current.type !== 'none') {
        const currentPosition = playbackPositionRef.current;
        // `props?.atPosition` will be new position when `props?.blockPlayAndSeek` undefined
        const newPosition = props?.atPosition ?? currentPosition; // This case occurs when clicking on Play button
        const blockedPosition = blockSeekAndPlayRef.current?.atPosition ?? 0;
        if (
          blockSeekAndPlayRef.current.type === 'all' ||
          (blockSeekAndPlayRef.current.type === 'forward' && newPosition > blockedPosition) ||
          (blockSeekAndPlayRef.current.type === 'backward' && newPosition < blockedPosition)
        ) {
          logger.debug('PLAY', '2193 PLAY BLOCKED');
          return;
        }

        // Reset blockSeekAndPlayRef when seeking to before the blocked position
        if (
          (blockSeekAndPlayRef.current.type === 'forward' && newPosition < blockedPosition) ||
          (blockSeekAndPlayRef.current.type === 'backward' && newPosition > blockedPosition)
        ) {
          blockSeekAndPlayRef.current.type = 'none';
        }
      }

      if (props?.blockPlayAndSeek === 'none') {
        // Clear block before we set play
        blockSeekAndPlayRef.current.type = props.blockPlayAndSeek;
      }

      if (play) {
        // Reset pause timer
        setPauseTimer(0);
        setPauseMessage(undefined);

        if (currentRoom.type === RoomType.Presentation) {
          if (props?.atPosition !== undefined) {
            logger.debug('PLAY', '1387 ISPLAYING JUMP TO ', props.atPosition);
            setPlaybackPosition(props.atPosition);
          }

          if (mode === PlayerMode.replay && playbackPositionRef.current >= ewebinar.duration) {
            setPlaybackPosition(0);
          }

          if (player) {
            // Make sure this happens synchronously since this maybe the first
            // time we're trying to play the video in replay mode
            player.play();
          }
        }
      } else {
        if (props?.secsToPause) {
          pauseTimerRemainingRef.current = null;
          setPauseTimer(props.secsToPause);
          setPauseMessage(props.message);
        }
        pausePlayer();

        if (props?.atPosition !== undefined) {
          logger.debug('PLAY', '2209 ISPLAYING JUMP TO ', props.atPosition);
          setPlaybackPosition(props.atPosition);
        }
      }

      logger.debug('PLAY', '2011 SETISPLAYING API ', play);
      setIsPlaying(play);

      if (props?.blockPlayAndSeek && props.blockPlayAndSeek !== 'none') {
        logger.debug('PLAY', `SETISPLAYING BLOCKING ${props.blockPlayAndSeek} PLAY/SEEK`);
        blockSeekAndPlayRef.current.type = props.blockPlayAndSeek;
        blockSeekAndPlayRef.current.atPosition = props.atPosition;
      }
    },
    [
      currentRoom.type,
      ewebinar.duration,
      mode,
      pausePlayer,
      player,
      setPauseTimer,
      setPlaybackPosition,
    ]
  );

  const setPlaybackPositionAPI = useDebouncedCallback(
    async (newPosition: number): Promise<void> => {
      if (pauseTimerRef.current > 0) {
        setPauseTimer(0);
        setPauseMessage(undefined);
        if (mode !== PlayerMode.edit) {
          setIsPlayingAPI(true);
        }
      }
      // Bound to domain
      newPosition = Math.min(rooms.domain[1], Math.max(rooms.domain[0], newPosition));
      await setPlaybackPosition(newPosition);
      logger.debug('PLAY', '267 SET EXTERNAL ', { newPosition });
    },
    100,
    { leading: true, maxWait: 200, trailing: true }
  );

  const setScrobblingAPI = React.useCallback(
    (scrobbling: boolean, position: number) => {
      setIsScrobbling(scrobbling);

      if (scrobbling) {
        scrobblingFromRef.current = position;
        return;
      }

      // Finished - call playbackEvent
      logger.debug('PLAY', '1525 Scrobbling done', {
        scrobblingFrom: scrobblingFromRef.current,
        scrobblingTo: position,
      });
    },
    [setIsScrobbling]
  );

  const wplayer: WebinarPlayerState = {
    editMode,
    colors,
    dataLoaded,
    playerIframeRef,
    webinarAreaRef,
    error,
    setError,
    errorCode,
    joinMessage,
    mode,
    set,
    ewebinar,
    attendee,
    attendeeId: attendee?.id,
    chatType: chatType,

    placement,
    isEmbeddedPlayer,
    isCollapsedEmbeddedPlayer,

    scrollToCardOrPositionTarget,
    scrollToCardOrPosition,
    scrollCardsToPosition,

    interactions,
    cardInteractions,
    cardInteractionsRef,
    nonCardInteractions,
    overlaysUnderControls,
    overlaysOverControls,
    overlaysOverArea,

    willBeRemovedCards,
    setWillBeRemovedCard,

    pinnedInteraction,
    interactionHeights,
    fastInteractionHeights,

    interactionHeightsVersion,
    markInteractionActive,
    markInteractionInactive,
    activeInteractionId,
    activeInteractionRef,
    setInteractionHeight,
    interactionInEdit,
    isEditingInteractions: Boolean(isEditingInteractions),
    interactionInEditModalId,
    setInteractionInEditModalId,
    hasAgenda,
    onInteractionIconClick,
    interactionFactory,
    totalReactions,
    includeLegacyInteractions,
    setIncludeLegacyInteractions,

    vimeoDuration,
    duration,
    rooms,
    currentRoom,

    loaded,
    domain,
    playbackPosition,
    playbackPositionRef,
    playbackPositionValue,
    setPlaybackPosition: setPlaybackPositionAPI,
    lastSegmentRef,
    setLastSegment,
    playbackSpeed,
    setPlaybackSpeed,
    focusedCardMode,
    setFocusedCardMode,
    stateModalClose,
    setStateModalClose,

    joinTime,
    hasJoined,
    setHasJoined,
    clickedJoin,

    hasInVideoFormShowBeforeWatching,
    showingInVideoFormBeforeWatching,

    showOverlay,
    setShowOverlay,

    isScrobbling,
    scrobblingFromRef,
    setScrobbling: setScrobblingAPI,

    setIsPlaying: setIsPlayingAPI,
    pauseTimer, // Full length of the current pauseTimer
    setPauseTimer,
    pauseTimerRemaining: pauseTimerRemainingRef.current, // When we initialize WebinarPlayer and there's already a pause timer, how much time is remaining?
    pauseMessage: pauseMessage ?? undefined,

    player,
    vimeoVideoId,
    vimeoHash,
    isPlaying,
    isBuffering,
    isSeeking,
    isSeekingRef,
    seekingFromRef,

    dimensions,
    dimensionsRef,

    transitions,

    timelineComponents,
    timelineHeight,
    headerSize: 24,
    muted,
    setMuted,
    mutedByJoiningIssue,
    setMutedByJoiningIssue,
    volume,
    setVolume,

    actionsPaneState,
    setActionsPaneState,

    setIsReplayExpired: setIsReplayExpired,
    isReplayExpired: isReplayExpired,
    inactiveReplay,
    onInactiveReplay,
    onRejoinReplay,

    connectionId,
    setConnectionId,
    shortIdRef,

    onZoomScaleChange,
    blocked,
    isInBackground,

    disableControlsHover,
    setDisableControlsHover,

    inTestMode,
    lastActiveDateRef,
    markUserActive,
    locale,
    interactionState,
    setInteractionState,
    agendaMenuShowed,
    setAgendaMenuShowed,
    attendeeCount,
    setAttendeeCount,

    enabledCC,
    setEnabledCC,
  };

  return wplayer;
};

const VideoEditControlsArea: React.FC<{ children?: React.ReactNode }> = withWebinarPlayerContext([
  'editMode',
  'interactionInEdit',
  'dimensions',
])(({ children, context }) => {
  const { editMode, interactionInEdit, dimensions } = context;

  return (
    <div
      id='VideoEditControlsArea'
      className={`flex flex-col actions-${dimensions.actionsPane.location}`}
      css={css`
        width: ${editMode &&
        ['edit', 'dash.interactions'].includes(editMode) &&
        !interactionInEdit /* Hack for main edit area */
          ? `calc(100% - ${dimensions.actionsPane.maxWidthRem}rem)`
          : '100%'};

        &.actions-bottom {
          height: ${dimensions.videoControlsArea.height}px;
        }
      `}
    >
      {children}
    </div>
  );
});

const VideoArea: React.FC = withWebinarPlayerContext([
  'ewebinar',
  'dimensions',
  'mode',
  'interactionInEdit',
  'isPlaying',
])(({ context }) => {
  const { ewebinar, mode, interactionInEdit, isPlaying } = context;

  // DD: Unclear why this is needed
  const anchorInEdit = interactionInEdit && !isPlaying ? interactionInEdit : undefined;

  const fontCssUrl = ewebinar?.fontCssUrl;
  const font = ewebinar?.font;

  React.useEffect(() => {
    if (fontCssUrl && fontCssUrl.length > 0) {
      importWithRetry(() => import('webfontloader')).then((WebFontLoader) => {
        WebFontLoader.load({
          custom: {
            families: [font!],
            urls: [fontCssUrl!],
          },
        });
      });
    }
  }, [fontCssUrl, font]);

  return (
    <div
      id='VideoControlsArea'
      className='VideoControlsArea relative'
      css={[
        tw`flex flex-1`,
        css`
          .room-font {
            font-family:
              '${ewebinar?.font || 'inherit'}',
              DM Sans;
          }
        `,
        mode == PlayerMode.edit
          ? tw`overflow-hidden`
          : css`
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
            `,
      ]}
    >
      <PinnedCardBar />
      <VideoControlsBar overVideo={false} />

      <div
        id={`agenda-tab-${anchorInEdit?.id}`}
        className='absolute top-0 bottom-0 left-0 right-0 control bottom-control pointer-events-none'
      />

      <RoomParts />
    </div>
  );
});

const WebinarArea: React.FC<{ hide?: boolean; children: React.ReactNode } & StyleProps> =
  withWebinarPlayerContext([
    'dimensions',
    'webinarAreaRef',
    'animations',
    'editMode',
    'actionsPaneState',
    'placement',
  ])(({
    className,
    hide,
    children,
    context: { dimensions, editMode, webinarAreaRef, animations, actionsPaneState, placement },
  }) => {
    const etheme = useThemeContext();

    const hasActionsPaneChanged = useHasChangedShallow([actionsPaneState], { skipInitial: true });
    React.useEffect(() => {
      if (!hasActionsPaneChanged) return;

      window.setTimeout(() => {
        let variants = [];
        switch (actionsPaneState) {
          case 'shown':
            variants = ['actions.open'];
            break;
          case 'hidden':
            variants = ['actions.close'];
            break;
          case 'offscreen':
            variants = ['actions.gone'];
            break;
        }

        logger.debug('ANIM', `ActionsPane -> ${actionsPaneState}, start `, variants);
        animations.start(variants);
      }, 750); // Wait for MessageThread and other components to update based on new dimensions values
    }, [actionsPaneState, animations, hasActionsPaneChanged]);

    // We doing this instead of using Helmet because for unknown reasons Helmet isn't removed when unmounted when using with React.lazy()
    React.useLayoutEffect(() => {
      const styleEl = document.createElement('style');
      styleEl.innerHTML = `
        body {
          background-color: ${
            placement === PlayerPlacement.EmbeddedPlayer ? 'transparent' : '#f3f3f3'
          } !important;
        }
      `;
      document.head.appendChild(styleEl);

      return () => {
        document.head.removeChild(styleEl);
      };
    }, [placement]);

    return (
      <div
        className={`WebinarArea ${className} ${hide ? 'hidden' : ''}`}
        css={css`
          ${tw`flex h-full flex-1`}
        `}
      >
        <motion.div
          animate={animations}
          id='WebinarArea'
          ref={webinarAreaRef}
          className={`
            actions-${dimensions.actionsPane.location}
          `}
          css={css`
            position: relative;
            display: flex;
            overflow: ${editMode === 'edit' ? 'auto' : 'hidden'};
            background: ${etheme.player.bg};

            width: 100%;
            height: 100%;

            &.actions-bottom .join-parent {
              flex-direction: column;
            }
          `}
        >
          <WaitFor element='joinRoom'>
            <JoinRoom>{children}</JoinRoom>
          </WaitFor>
        </motion.div>
      </div>
    );
  });

interface ActionsPaneOverlayProps {
  location: WebinarDimensions['actionsPane']['location'];
  transitions: Transitions;
  maxWidthRem: number;
  actionsTopPx: number;
}
const ActionsPaneOverlay = withContextValue<
  WebinarPlayerUIState,
  ActionsPaneOverlayProps,
  {
    location: WebinarDimensions['actionsPane']['location'];
    maxWidthRem: number;
    actionsTopPx: number;
    transitions: Transitions;
  }
>(
  WebinarPlayerContext,
  ({ dimensions, transitions }) => ({
    location: dimensions.actionsPane.location,
    maxWidthRem: dimensions.actionsPane.maxWidthRem || dimensions.actionsPane.bottomWidthRem,
    actionsTopPx: dimensions.actionsPane.actionsTopPx,
    transitions,
  }),
  'ActionsPaneOverlay'
)(({ location, maxWidthRem, actionsTopPx, transitions }) => {
  return (
    <motion.div
      id='actionsOverlay'
      className={`z-player-acitonspane-overlay
        ${location === 'bottom' && 'actions-bottom'}
        ${isMobile && 'is-mobile'}
      `}
      css={css`
        position: absolute;
        pointer-events: none;
        top: 0;
        bottom: 0;
        right: 0;
        width: ${maxWidthRem}rem;
        backdrop-filter: blur(0px);

        .RegistrationFormModal {
          width: ${maxWidthRem}rem !important;
          left: unset !important;
          & > div {
            align-items: flex-start !important;
            height: 100%;
          }
          &.showblur {
            backdrop-filter: blur(0) !important;
            background: transparent !important;
          }
        }

        &.is-mobile {
          .Modal {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 100% !important;
            right: 0 !important;
          }
        }

        .Modal {
          width: calc(100% - 1rem) !important;
          max-width: calc(100% - 1rem) !important;
          min-width: calc(100% - 1rem) !important;
          right: 0.5rem !important;

          max-height: 100vh !important;

          .button-control.modal-close {
            margin-right: -0.25rem;
          }
          .grecaptcha-badge {
            visibility: hidden;
          }
          .modal-footer {
            padding-right: 1rem !important;
          }
        }
      `}
      transition={transitions['actions.overlay']}
      variants={{
        'actions.overlay.open': {
          opacity: 1,
          WebkitBackdropFilter: 'blur(10px)',
          backdropFilter: `blur(10px)`,
          pointerEvents: 'all',
        },
        'actions.overlay.close': {
          opacity: 1,
          WebkitBackdropFilter: 'blur(0px)',
          backdropFilter: `blur(0px)`,
          pointerEvents: 'none',
        },
        'actions.close': {
          opacity: 0,
          pointerEvents: 'none',
        },
        ...(!isMobile
          ? {
              'cards.focused.on': {
                y: 0,
                transition: transitions['card.focus'],
              },
              'cards.focused.off': {
                y: -actionsTopPx,
                transition: transitions['card.focus'],
              },
            }
          : {}),
      }}
    />
  );
});

const ActionsPane: React.FC<{ children?: React.ReactNode }> = withWebinarPlayerContext(
  [
    'mode',
    'currentRoom',
    'ewebinar',
    'set',
    'attendeeId',
    'isReplayExpired',
    'dimensions',
    'focusedCardMode',
    'animations',
    'setFocusedCardMode',
    'actionsPaneState',
    'cardInteractions',
    'nonCardInteractions',
    'setActionsPaneState',
    'transitions',
  ],
  'ActionsPane'
)(({ children, context }) => {
  const {
    mode,
    currentRoom,
    set,
    actionsPaneState,
    attendeeId,
    isReplayExpired,
    dimensions,
    focusedCardMode,
    animations,
    cardInteractions,
    nonCardInteractions,
    setFocusedCardMode,
    setActionsPaneState,
    ewebinar,
    transitions,
  } = context;
  const { authorized } = useSSOAuth(set, attendeeId);

  const hasTopChanged = useHasChangedShallow([dimensions.actionsPane.actionsTopPx]);
  logger.debug('ANIM', 'start ', { hasTopChanged });
  React.useEffect(() => {
    if (!hasTopChanged) return;

    // We have to rerun this animation if the height changes
    logger.debug('ANIM', { actionsTop: dimensions.actionsPane.actionsTopPx });

    if (focusedCardMode && dimensions.actionsPane.location === 'right') {
      setFocusedCardMode(false);
    }

    if (focusedCardMode) {
      logger.debug('ANIM', '2136 start ', ['cards.focused.on']);
      animations.start('cards.focused.on', transitions['video.resize']);
    } else {
      logger.debug('ANIM', '2139 start ', ['cards.focused.off']);
      animations.start('cards.focused.off', transitions['video.resize']);
    }
  }, [
    animations,
    dimensions.actionsPane.actionsTopPx,
    dimensions.actionsPane.location,
    focusedCardMode,
    hasTopChanged,
    setFocusedCardMode,
    transitions,
  ]);

  React.useEffect(() => {
    if (cardInteractions.length === 0) {
      setActionsPaneState('offscreen');
    }
  }, [cardInteractions, setActionsPaneState]);

  // Prevent Right bar show when Post Exit Room
  if (
    mode !== PlayerMode.edit &&
    (!authorized ||
      !currentRoom.caps.hasInteractions ||
      isReplayExpired ||
      (cardInteractions.length === 0 && nonCardInteractions.length === 0)) // also check nonCardInteractions for Private Message)
  ) {
    return null;
  }

  if ([PlayerMode.live, PlayerMode.replay].includes(mode)) {
    const width =
      dimensions.actionsPane.location === 'bottom'
        ? `${dimensions.actionsPane.bottomWidthRem}rem`
        : `${dimensions.actionsPane.maxWidthRem}rem`; // Always use max so card notifications are visible

    return (
      <>
        <motion.div
          key='ActionsPane'
          id='ActionsPane'
          className={`ActionsPane
            actions-${dimensions.actionsPane.location}
          `}
          css={css`
            font-family: '${ewebinar.font || 'unset'}';

            ${focusedCardMode ? tw`z-player-actions-pane-focused` : tw`z-player-actions-pane`}
            pointer-events: none;
            position: absolute;
            top: 0;
            right: 0;

            display: flex;
            flex-direction: column;

            &.actions-right {
              bottom: 0;
              width: ${width};
            }

            &.actions-bottom {
              left: 0;
              bottom: ${dimensions.chat.paddingBottomRem}rem;
              overflow: hidden ${/* On mobile if not hidden it goes over player controls */ ''};
            }
          `}
          transition={transitions['video.resize']}
          animate={{ y: dimensions.actionsPane.actionsTopPx }}
          exit={{
            opacity: 0,
            x: `${dimensions.actionsPane.maxWidthRem}rem`,
          }}
          variants={{
            'cards.focused.on': {
              y: 0,
              transition: transitions['card.focus'],
            },
            'cards.focused.off': {
              y: dimensions.actionsPane.actionsTopPx,
              transition: transitions['card.focus'],
            },
            'actions.overlay.open': {
              zIndex: parseInt(theme`zIndex.player-actions-pane-focused`.toString(), 10),
              transition: transitions['card.focus'],
            },
            'actions.overlay.close': {
              zIndex: parseInt(theme`zIndex.player-actions-pane`.toString(), 10),
              transition: transitions['card.focus'],
            },
          }}
        >
          <ActionsPaneOverlay />

          <div id='confetti' />

          <div
            className='relative flex flex-col justify-start w-full h-full'
            css={css`
              pointer-events: none;
            `}
          >
            {children}
          </div>
        </motion.div>

        {[PlayerMode.replay, PlayerMode.live].includes(mode) && (
          <>
            {dimensions.actionsPane.location !== 'bottom' && (
              <BackgroundGradient
                placement='bottom'
                width={
                  actionsPaneState === 'shown'
                    ? `${dimensions.actionsPane.maxWidthRem}rem`
                    : actionsPaneState === 'hidden'
                      ? `${dimensions.actionsPane.idock.widthRem}rem`
                      : '0rem'
                }
                height={`${dimensions.chat.heightPx + 1 * 14}px`}
                className='white-out-bottom z-player-bottom-whiteout'
              />
            )}
          </>
        )}

        <ActionsPaneToggleButton />
      </>
    );
  }

  // mode === edit
  return (
    <div
      className={`ActionsPane
        ${isMobile && 'mobile'}
        ${mode === PlayerMode.edit && 'editing'}
      `}
      css={[
        tw`flex flex-col`,
        css`
          font-family: '${ewebinar.font || 'inherit'}', 'DM Sans';

          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;

          &.mobile {
            height: 100%;
          }

          &.editing {
            width: ${dimensions.actionsPane.rightWidthRem}rem;
          }
        `,
      ]}
    >
      {children}
    </div>
  );
});

const InteractionShortcuts = withWebinarPlayerContext(['interactions', 'setPlaybackPosition'])(({
  context,
}) => {
  const { interactions, setPlaybackPosition } = context;
  return (
    <>
      {interactions
        .filter((i) => i.details?.shortcut)
        .map((interaction) => (
          <Shortcut
            key={interaction.details!.shortcut!}
            allowDefault={true}
            held={['Control']}
            ordered={[interaction.details!.shortcut!.substring(0, 1) as Key]}
            onMatch={(): void => {
              console.log(
                'SHORTCUT HIT ALT+',
                interaction.details!.shortcut!.substring(0, 1) as Key
              );
              setPlaybackPosition(interaction.playbackPosition);
            }}
          />
        ))}
    </>
  );
});

interface WebinarPlayerProps extends StyleProps {
  hideTimelineComponents?: (keyof TimelineComponents)[];
  showTimelineComponents?: (keyof TimelineComponents)[];
  isEditingInteractions?: boolean;
  interactionInEdit?: InteractionInfo;
  mode: PlayerMode;
  placement?: PlayerPlacement;
  set: PublicSetFragment;
  ewebinar: PublicEWebinarFragment;
  attendee?: AttendeeFragment;
  inTestMode?: boolean;
  editMode?: WebinarEditMode;
  onInteractionIconClick?: (id: string) => void;
  onZoomScaleChange?: (domain: [number, number]) => void;
}

const WebinarPlayerBase: React.FC<React.PropsWithChildren<WebinarPlayerProps>> = ({
  set,
  ewebinar,
  attendee,
  hideTimelineComponents,
  showTimelineComponents,
  mode = PlayerMode.live,
  placement = PlayerPlacement.EWebinarPage,
  editMode,
  onInteractionIconClick,
  onZoomScaleChange,
  children,
  interactionInEdit,
  isEditingInteractions,
  inTestMode,
}) => {
  if (interactionInEdit) {
    isEditingInteractions = true;
  }

  const { FBPixel, FBPixelInitialized } = useFBPixelContext();
  const joinedSessionTracked = React.useRef(false);

  const webinarPlayer = useWebinarPlayer({
    hideTimelineComponents,
    showTimelineComponents,
    mode,
    placement,
    editMode,
    set,
    ewebinar,
    attendee,
    onInteractionIconClick,
    onZoomScaleChange,
    isEditingInteractions,
    interactionInEdit,
    inTestMode,
  });

  const hasJoined = webinarPlayer.hasJoined;
  React.useEffect(() => {
    if (!attendee) {
      return;
    }

    switch (mode) {
      case PlayerMode.edit:
        return;
      case PlayerMode.live:
        if (hasJoined === HasJoined.Joined && FBPixelInitialized && !joinedSessionTracked.current) {
          joinedSessionTracked.current = true;
          FBPixel.pageView();
          FBPixel.trackCustom(FBPixelEvent.JoinedSession, {
            email: attendee?.email,
            type: attendee?.sessionType,
          });
        }
        return;
      case PlayerMode.replay:
        if (FBPixelInitialized) {
          FBPixel.pageView();
          FBPixel.trackCustom(FBPixelEvent.JoinedSession, {
            email: attendee?.email,
            type: attendee?.sessionType,
          });
        }
    }
  }, [hasJoined, attendee, mode, FBPixelInitialized, FBPixel]);

  const animations = useAnimationControls();

  if (attendee && !webinarPlayer.attendee) return <Loading />;
  if (!webinarPlayer.ewebinar) return <Loading />;

  return (
    <WebinarPlayerContext.Provider value={{ ...webinarPlayer, animations }}>
      <PropertyFilledDataContextProvider>
        {false && <InteractionShortcuts />}
        {children}
      </PropertyFilledDataContextProvider>
    </WebinarPlayerContext.Provider>
  );
};

const WebinarPlayer: React.FC<React.PropsWithChildren<WebinarPlayerProps>> = (props) => {
  const attendeeRef = React.useRef(props.attendee);

  const cachedAttendee = React.useMemo(() => {
    const attendeeChanged =
      !attendeeRef.current || !isEqualDeep(attendeeRef.current, props.attendee);

    if (attendeeChanged) {
      attendeeRef.current = props.attendee;
    }

    return attendeeRef.current;
  }, [props.attendee]);

  return (
    <AttendeePropertiesProvider attendee={props?.attendee}>
      <WebinarPlayerRenderProvider>
        <WebinarPlayerBase {...props} attendee={cachedAttendee} />
      </WebinarPlayerRenderProvider>
    </AttendeePropertiesProvider>
  );
};

export default withProperties(WebinarPlayer, {
  ActionsPane,
  WebinarArea,
  Mode: PlayerMode,
  Rooms,
  Timeline,
  VideoEditControlsArea,
  VideoArea,
  InteractionsStream,
});
